import React from 'react'
import {
  DndContext,
  type DragCancelEvent,
  type DragEndEvent,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  Alert,
  Box,
  Button,
  Paper,
  Skeleton,
  Snackbar,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useOutletContext, useSearchParams } from 'react-router-dom'
import CategoryPills from '../../components/prep/CategoryPills'
import PrepCard from '../../components/prep/PrepCard'
import AddPrepToShowClockModal from '../../components/prep/AddPrepToShowClockModal'
import PrepRecoveryShell from '../../components/prep/PrepRecoveryShell'
import PrepDetailDrawer from '../../components/prep/PrepDetailDrawer'
import PrepListViewport from '../../components/prep/PrepListViewport'
import PrepGrid from '../../components/prep/PrepGrid'
import usePrepListStore, {
  type DismissUndoSnapshot,
  type PrepListPreviousView,
  type PrepListState,
  selectMyItemsLoading,
} from '../../store/prepListStore'
import useUserSettingsStore, { clampSplit, MAX_SPLIT, MIN_SPLIT } from '../../store/userSettings'
import useStationStore from '../../store/station'
import usePrepRecoveryStore from '../../store/prepRecoveryStore'
import useShowClockPrepUsageStore from '../../store/showClockPrepUsageStore'
import usePrepDashboardStore from '../../store/prepDashboardStore'
import usePrepUsageStore from '../../store/prepUsageStore'
import {
  markDashboardMyPrepDirty,
  syncAuthoritativeMyPrepOrder,
} from '../../store/prepMembershipSync'
import {
  beginPrepUsedOptimistic,
  beginPrepUsedUndoOptimistic,
  commitPrepUsed,
  commitPrepUsedUndo,
  rollbackPrepUsed,
  rollbackPrepUsedUndo,
  type PrepUsedMutationSnapshot,
} from '../../store/prepUsedSync'
import { usePageTitle } from '../../components/common/PageTitleContext'
import { shapeBorderRadius } from '../../theme/helpers'
import {
  addPrepItemToMyPrep,
  archivePrepItem,
  dismissPrepItem,
  fetchMyPrepList,
  fetchPrepCategories,
  fetchPrepItemDetail,
  getMyPrepCount,
  removePrepItemFromMyPrep,
  reorderMyPrep as reorderMyPrepApi,
  searchPrepItems,
  undoDismissPrepItem,
  type PrepCategoryWithCount,
  type PrepDetailResponse,
  type PrepListItem,
  type MyPrepListItemsResponse,
  type PrepSearchParams,
} from '../../api/prep'
import PrepDetailContent from '../../components/prep/PrepDetailContent'
import type { MeResponse } from '../../api/me'
import {
  PREP_FETCH_RETRY_DELAYS_MS,
  computeRetryDelay,
  isRetriablePrepFetchError,
  wait,
} from '../../components/prep/dashboard/utils'
import {
  resolveSelectionForMyOnly,
  resolveSelectionForSingleLayout,
  selectNextAfterRemoval,
  shouldOpenDrawerOnMobile,
  type SelectionResolution,
} from './PrepViewOrchestration'
import { resolveServerTotal } from '../../utils/prepCounts'

const PAGE_SIZE = 30
const IDLE_PREFETCH_BATCH = 8
const SCROLL_ABORT_THRESHOLD = 800

type OutletContext = { me: MeResponse; includeSuperadmin: boolean }

type SnackbarAction = 'undo-dismiss' | 'undo-my-remove' | 'undo-used' | null

interface SnackbarState {
  message: string
  severity: 'success' | 'error' | 'warning'
  action?: SnackbarAction
}

interface PrepListUsedUndoSnapshot {
  mutation: PrepUsedMutationSnapshot
  itemsIndex: number
  myItemsIndex: number
}

type CommitPayload = {
  mode: 'my-only' | 'single'
  before: number | null
  after: number | null
  visibleLen: number
}

type EnterMyOnlySelection = Parameters<PrepListState['enterMyOnly']>[0]

const collectCategorySlugs = (item: PrepListItem): string[] => {
  const slugs = new Set<string>()
  if (item.primary_category?.slug) {
    slugs.add(item.primary_category.slug.toLowerCase())
  }
  if (Array.isArray(item.categories)) {
    item.categories.forEach((category) => {
      if (category?.slug) {
        slugs.add(category.slug.toLowerCase())
      }
    })
  }
  return Array.from(slugs)
}

const sortItemsByIdOrder = (items: PrepListItem[], order: number[]): PrepListItem[] => {
  if (!Array.isArray(items) || items.length === 0) {
    return []
  }

  const orderMap = new Map<number, number>()
  order.forEach((id, index) => {
    if (!orderMap.has(id)) {
      orderMap.set(id, index)
    }
  })

  return [...items].sort((a, b) => {
    const indexA = orderMap.get(a.id)
    const indexB = orderMap.get(b.id)
    if (indexA === undefined && indexB === undefined) {
      return a.id - b.id
    }
    if (indexA === undefined) {
      return 1
    }
    if (indexB === undefined) {
      return -1
    }
    return indexA - indexB
  })
}

const itemsMatchOrder = (a: PrepListItem[], b: PrepListItem[]): boolean => {
  if (a === b) {
    return true
  }
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) {
    return false
  }
  for (let index = 0; index < a.length; index += 1) {
    if (a[index]?.id !== b[index]?.id) {
      return false
    }
  }
  return true
}

function MyPrepListSkeleton({ rows = 8 }: { rows?: number }) {
  return (
    <Box sx={{ px: 1 }}>
      {Array.from({ length: rows }).map((_, index) => (
        <Skeleton
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          variant="rounded"
          height={56}
          sx={{ mb: 1.25, borderRadius: 2 }}
        />
      ))}
    </Box>
  )
}

interface SortablePrepCardProps {
  item: PrepListItem
  lineClamp: number
  isSuperadmin: boolean
  onOpenDetail: (id: number) => void
  onToggleMyPrep: (item: PrepListItem, next: boolean) => Promise<void>
  onDismiss: (item: PrepListItem) => Promise<void>
  onArchive?: (item: PrepListItem) => Promise<void>
  onAddToShowClock: (params: { prepId: number; prepHeadline: string; launchedFromMyPrep: boolean }) => void
  launchedFromMyPrep: boolean
  onHoverPrefetch?: (id: number) => void
  selected?: boolean
  disableReorder: boolean
  onKeyboardReorder: (id: number, direction: 'up' | 'down') => void
}

const MyPrepSortableCard: React.FC<SortablePrepCardProps> = ({
  item,
  lineClamp,
  isSuperadmin,
  onOpenDetail,
  onToggleMyPrep,
  onDismiss,
  onArchive,
  onAddToShowClock,
  launchedFromMyPrep,
  onHoverPrefetch,
  selected,
  disableReorder,
  onKeyboardReorder,
}) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id: item.id, disabled: disableReorder })

  const style: React.CSSProperties = {
    transform: transform ? CSS.Transform.toString(transform) : undefined,
    transition,
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      onKeyboardReorder(item.id, 'up')
    } else if (event.key === 'ArrowDown') {
      event.preventDefault()
      onKeyboardReorder(item.id, 'down')
    }
  }

  return (
    <Box ref={setNodeRef} style={style} sx={{ touchAction: 'none' }}>
      <PrepCard
        item={item}
        lineClamp={lineClamp}
        isSuperadmin={isSuperadmin}
        onOpenDetail={onOpenDetail}
        onToggleMyPrep={onToggleMyPrep}
        onDismiss={onDismiss}
        onArchive={onArchive}
        onAddToShowClock={onAddToShowClock}
        launchedFromMyPrep={launchedFromMyPrep}
        onHoverPrefetch={onHoverPrefetch}
        selected={selected}
        dragHandle={{
          attributes,
          listeners,
          setActivatorNodeRef,
          tabIndex: disableReorder ? -1 : 0,
          disabled: disableReorder,
          onKeyDown: handleKeyDown,
          dragging: isDragging,
        }}
      />
    </Box>
  )
}

const extractRequestId = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object') {
    return undefined
  }

  const withRequest = error as { request_id?: unknown; requestId?: unknown; data?: unknown }

  if (typeof withRequest.request_id === 'string') {
    return withRequest.request_id
  }
  if (typeof withRequest.requestId === 'string') {
    return withRequest.requestId
  }

  if (withRequest.data && typeof withRequest.data === 'object') {
    const data = withRequest.data as { request_id?: unknown; requestId?: unknown }
    if (typeof data.request_id === 'string') {
      return data.request_id
    }
    if (typeof data.requestId === 'string') {
      return data.requestId
    }
  }

  return undefined
}

const withRequestId = (message: string, requestId?: string): string => {
  if (!requestId) {
    return message
  }
  return `${message} (Request ID: ${requestId})`
}

if (process.env.NODE_ENV !== 'production' && typeof window !== 'undefined') {
  const globalScope = window as unknown as Record<string, unknown>
  const evaluationKey = '__RPA_PREP_LIST_PAGE_EVAL__'
  const previousEvaluations =
    typeof globalScope[evaluationKey] === 'number' ? (globalScope[evaluationKey] as number) : 0
  globalScope[evaluationKey] = previousEvaluations + 1

  if (previousEvaluations > 0 && typeof console !== 'undefined' && typeof console.warn === 'function') {
    console.warn('[prep] PrepListPage module evaluated more than once', previousEvaluations + 1)
  }
}

const PrepListPage: React.FC = () => {
  usePageTitle('Prep Sheet', { showBreadcrumbs: false })
  const { me, includeSuperadmin } = useOutletContext<OutletContext>()
  const [searchParams, setSearchParams] = useSearchParams()

  const selectedCategories = usePrepListStore((state) => state.selectedCategories)
  const mode = usePrepListStore((state) => state.mode)
  const search = usePrepListStore((state) => state.search)
  const setSearch = usePrepListStore((state) => state.setSearch)
  const lineClamp = usePrepListStore((state) => state.lineClamp)
  const scrollResetToken = usePrepListStore((state) => state.scrollResetToken)
  const setCursorInStore = usePrepListStore((state) => state.setCursor)
  const setMode = usePrepListStore((state) => state.setMode)
  const myOnly = usePrepListStore((state) => state.myOnly)
  const exitMyOnly = usePrepListStore((state) => state.exitMyOnly)
  const myIdsLoading = usePrepListStore((state) => state.myIdsLoading)
  const myItemsHydrationLoading = usePrepListStore(selectMyItemsLoading)
  const myIds = usePrepListStore((state) => state.myIds)
  const storeMyItems = usePrepListStore((state) => state.myItems)
  const myCount = usePrepListStore((state) => state.myCount)
  const ensureFullMyIds = usePrepListStore((state) => state.ensureFullMyIds)
  const syncMyIdsFromItems = usePrepListStore((state) => state.syncMyIdsFromItems)
  const updateMyItemInStore = usePrepListStore((state) => state.updateMyItem)
  const setMyCount = usePrepListStore((state) => state.setMyCount)
  const applyMyCategoryCounts = usePrepListStore((state) => state.applyMyCategoryCounts)
  const incMyCount = usePrepListStore((state) => state.incMyCount)
  const decMyCount = usePrepListStore((state) => state.decMyCount)
  const incMyCategoryCounts = usePrepListStore((state) => state.incMyCategoryCounts)
  const decMyCategoryCounts = usePrepListStore((state) => state.decMyCategoryCounts)
  const myOrderedIds = usePrepListStore((state) => state.myOrderedIds)
  const setMyOrderedIds = usePrepListStore((state) => state.setMyOrderedIds)
  const myPerPageInStore = usePrepListStore((state) => state.myPerPage)
  const myReordering = usePrepListStore((state) => state.myReordering)
  const setPendingMyUndo = usePrepListStore((state) => state.setPendingMyUndo)
  const applyMyRemovalOptimistic = usePrepListStore((state) => state.applyMyRemovalOptimistic)
  const applyMyUndoRestore = usePrepListStore((state) => state.applyMyUndoRestore)
  const pendingMyUndo = usePrepListStore((state) => state.pendingMyUndo)
  const setSelectedPrepIdInStore = usePrepListStore((state) => state.setSelectedPrepId)
  const theme = useTheme()
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'))
  const layout = useUserSettingsStore((state) => state.prep.layout)
  const recoveryOpen = usePrepRecoveryStore((state) => state.open)
  const recoveryMode = usePrepRecoveryStore((state) => state.mode)
  const setRecoveryOpen = usePrepRecoveryStore((state) => state.setOpen)
  const setRecoveryMode = usePrepRecoveryStore((state) => state.setMode)
  const singleLayoutSplit = useUserSettingsStore((state) => state.prep.singleLayoutSplit)
  const setSingleLayoutSplit = useUserSettingsStore((state) => state.setSingleLayoutSplit)
  const setPrepLayout = useUserSettingsStore((state) => state.setPrepLayout)
  const prepAgeMaxDays = useUserSettingsStore((state) => state.prep.ageMaxDays)
  const prepDateBasis = useUserSettingsStore((state) => state.prep.dateBasis)
  const searchIncludeDismissed = useUserSettingsStore(
    (state) => state.prep.searchIncludeDismissed,
  )
  const baseLayout = isMdUp ? layout : 'columns'
  const effectiveLayout = recoveryOpen && baseLayout === 'dashboard' ? 'columns' : baseLayout
  const handleRecoveryCategoriesLoading = React.useCallback(() => {
    setRecoveryCategoryState((prev) => ({ ...prev, loading: true, error: null }))
  }, [])
  const handleRecoveryCategoriesUpdate = React.useCallback((categories: PrepCategoryWithCount[]) => {
    setRecoveryCategoryState({ loading: false, items: categories, error: null })
  }, [])

  React.useEffect(() => {
    if (myOnly && layout === 'columns') {
      setPrepLayout('single')
    }
  }, [layout, myOnly, setPrepLayout])

  React.useEffect(() => {
    const controller = new AbortController()

    getMyPrepCount({ signal: controller.signal })
      .then((response) => {
        if (controller.signal.aborted) {
          return
        }
        const count = resolveServerTotal(response, undefined)
        setMyCount(count)
      })
      .catch((error) => {
        if (controller.signal.aborted) {
          return
        }
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.error('[prep] Failed to load My Prep count', error)
        }
      })

    return () => {
      controller.abort()
    }
  }, [setMyCount])

  const [searchInput, setSearchInput] = React.useState(search)
  React.useEffect(() => {
    setSearchInput(search)
  }, [search])

  React.useEffect(() => {
    const handle = window.setTimeout(() => {
      setSearch(searchInput.trimStart())
    }, 250)
    return () => {
      window.clearTimeout(handle)
    }
  }, [searchInput, setSearch])

  const isSuperadmin = React.useMemo(() => {
    const roleSet = new Set<string>()
    me.roles?.forEach((role) => roleSet.add(role))
    me.user?.roles?.forEach((role) => roleSet.add(role))
    return includeSuperadmin || roleSet.has('superadmin')
  }, [includeSuperadmin, me.roles, me.user?.roles])

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }
    const userId = me.user?.id
    if (!userId) {
      return
    }
    const storageKey = `rpa_prep_recovery_mode_${userId}`
    try {
      const stored = window.localStorage.getItem(storageKey)
      if (!stored) {
        return
      }
      if (stored === 'dismissed_all' && !isSuperadmin) {
        setRecoveryMode('dismissed')
        return
      }
      if (stored === 'dismissed' || stored === 'used' || stored === 'dismissed_all') {
        setRecoveryMode(stored)
      }
    } catch {
      // ignore localStorage errors
    }
  }, [isSuperadmin, me.user?.id, setRecoveryMode])

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }
    const userId = me.user?.id
    if (!userId) {
      return
    }
    const storageKey = `rpa_prep_recovery_mode_${userId}`
    try {
      window.localStorage.setItem(storageKey, recoveryMode)
    } catch {
      // ignore localStorage errors
    }
  }, [recoveryMode, me.user?.id])

  const stationId = useStationStore((state) => state.activeStation?.id ?? 0)
  const ensureShowClockUsage = useShowClockPrepUsageStore((state) => state.ensureUsage)
  const markUsed = usePrepUsageStore((state) => state.markUsed)
  const undoUsed = usePrepUsageStore((state) => state.undoUsed)

  const [snackbar, setSnackbar] = React.useState<SnackbarState | null>(null)
  const [undoState, setUndoState] = React.useState<DismissUndoSnapshot | null>(null)
  const [usedUndoState, setUsedUndoState] = React.useState<PrepListUsedUndoSnapshot | null>(null)

  const [addToShowClockTarget, setAddToShowClockTarget] = React.useState<{
    prepId: number
    prepHeadline: string
    launchedFromMyPrep: boolean
  } | null>(null)

  const handleRecoveryToggle = React.useCallback(() => {
    if (recoveryOpen) {
      setRecoveryOpen(false)
      return
    }

    if (myOnly) {
      exitMyOnly()
    }

    setRecoveryOpen(true)
  }, [exitMyOnly, myOnly, recoveryOpen, setRecoveryOpen])

  const handleOpenAddToShowClock = React.useCallback(
    (params: { prepId: number; prepHeadline: string; launchedFromMyPrep: boolean }) => {
      setAddToShowClockTarget(params)
    },
    [],
  )

  React.useEffect(() => {
    if (recoveryOpen && myOnly) {
      exitMyOnly()
    }
  }, [exitMyOnly, myOnly, recoveryOpen])

  const [categoryState, setCategoryState] = React.useState<{
    loading: boolean
    items: PrepCategoryWithCount[]
    error: string | null
  }>({ loading: true, items: [], error: null })
  const [recoveryCategoryState, setRecoveryCategoryState] = React.useState<{
    loading: boolean
    items: PrepCategoryWithCount[]
    error: string | null
  }>({ loading: false, items: [], error: null })
  const [categoryRetryReady, setCategoryRetryReady] = React.useState(false)
  const [categoryReloadToken, setCategoryReloadToken] = React.useState(0)
  const categoryRetryTimerRef = React.useRef<number | null>(null)
  const lastGoodCategoriesRef = React.useRef<PrepCategoryWithCount[]>([])
  const handleCategoryManualRetry = React.useCallback(() => {
    setCategoryRetryReady(false)
    setCategoryReloadToken((token) => token + 1)
  }, [])

  const [itemsState, setItemsState] = React.useState<{
    items: PrepListItem[]
    loading: boolean
    loadingMore: boolean
    hasMore: boolean
    cursor: string | null
    error: string | null
  }>({ items: [], loading: true, loadingMore: false, hasMore: false, cursor: null, error: null })

  const [myItemsState, setMyItemsState] = React.useState<{
    allItems: PrepListItem[]
    items: PrepListItem[]
    loading: boolean
    error: string | null
  }>({ allItems: [], items: [], loading: false, error: null })
  const [myHydrated, setMyHydrated] = React.useState(false)

  const listItems = myOnly ? myItemsState.items : itemsState.items
  const listAllMyItems = myItemsState.allItems
  const myItemsLocalLoading = myItemsState.loading
  const myOnlyLoading = myOnly ? myItemsHydrationLoading || myItemsLocalLoading : false
  const listLoading = myOnly ? myOnlyLoading : itemsState.loading
  const listError = myOnly ? myItemsState.error : itemsState.error
  const hasMore = myOnly ? false : itemsState.hasMore
  const loadingMore = myOnly ? false : itemsState.loadingMore
  const normalizedMyPerPage = Number.isFinite(myPerPageInStore)
    ? Math.max(3, Math.min(15, Math.floor(myPerPageInStore)))
    : 10
  const myPrepSkeletonRows = Math.min(10, Math.max(8, normalizedMyPerPage))
  const skeletonCount = 4

  React.useEffect(() => {
    if (!stationId || stationId <= 0) {
      return
    }
    const ids = listItems.map((item) => item.id)
    if (ids.length === 0) {
      return
    }
    void ensureShowClockUsage(stationId, ids)
  }, [ensureShowClockUsage, listItems, stationId])

  const lastClickedIdRef = React.useRef<number | null>(null)
  const selectionFrameRef = React.useRef<number | null>(null)
  const selectionMicrotaskScheduledRef = React.useRef(false)
  const pendingSelectionRef = React.useRef<
    | {
        mode: 'my-only' | 'single'
        before: number | null
        after: number | null
        visibleLen: number
      }
    | null
  >(null)

  const setSelectedPrepId = React.useCallback(
    (id: number | null, options?: { replace?: boolean }) => {
      setSelectedPrepIdInStore(id, options)
      const current = searchParams.get('prepId')
      if (id === null) {
        if (!current) {
          return
        }
        const next = new URLSearchParams(searchParams)
        next.delete('prepId')
        setSearchParams(next, { replace: options?.replace ?? false })
        return
      }
      const nextValue = String(id)
      if (current === nextValue) {
        return
      }
      const next = new URLSearchParams(searchParams)
      next.set('prepId', nextValue)
      setSearchParams(next, { replace: options?.replace ?? false })
    },
    [searchParams, setSearchParams, setSelectedPrepIdInStore],
  )

  const cancelSelectionTasks = React.useCallback(() => {
    if (
      typeof window !== 'undefined' &&
      selectionFrameRef.current !== null &&
      typeof window.cancelAnimationFrame === 'function'
    ) {
      window.cancelAnimationFrame(selectionFrameRef.current)
    }
    selectionFrameRef.current = null
    selectionMicrotaskScheduledRef.current = false
    pendingSelectionRef.current = null
  }, [])

  const commitSelectionUpdate = React.useCallback(
    (payload: CommitPayload, apply: (id: number | null, opts?: { replace?: boolean }) => void) => {
      apply(payload.after, { replace: true })
    },
    [],
  )

  const flushSelectionTasks = React.useCallback(() => {
    selectionFrameRef.current = null
    selectionMicrotaskScheduledRef.current = false
    const payload = pendingSelectionRef.current
    pendingSelectionRef.current = null
    if (!payload || payload.after === payload.before) {
      return
    }
    commitSelectionUpdate(payload, setSelectedPrepId)
  }, [commitSelectionUpdate, setSelectedPrepId])

  const scheduleSelectionUpdate = React.useCallback(
    (mode: 'my-only' | 'single', before: number | null, after: number | null, visibleLen: number) => {
      if (before === after) {
        return
      }

      pendingSelectionRef.current = { mode, before, after, visibleLen }

      if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
        if (selectionFrameRef.current !== null && typeof window.cancelAnimationFrame === 'function') {
          window.cancelAnimationFrame(selectionFrameRef.current)
        }
        selectionFrameRef.current = window.requestAnimationFrame(() => {
          flushSelectionTasks()
        })
        return
      }

      if (selectionMicrotaskScheduledRef.current) {
        return
      }
      selectionMicrotaskScheduledRef.current = true

      if (typeof queueMicrotask === 'function') {
        queueMicrotask(() => {
          flushSelectionTasks()
        })
      } else {
        Promise.resolve().then(() => {
          flushSelectionTasks()
        })
      }
    },
    [flushSelectionTasks],
  )

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )
  const [ariaLiveMessage, setAriaLiveMessage] = React.useState('')
  const searchActive = search.trim().length > 0
  const hasCategoryFilter = selectedCategories.length > 0
  const canReorder =
    myOnly &&
    !listLoading &&
    !myReordering &&
    listAllMyItems.length > 1 &&
    !searchActive &&
    !hasCategoryFilter
  const reorderDisabled = !canReorder

  const filterMyItems = React.useCallback(
    (items: PrepListItem[], orderOverride?: number[]): PrepListItem[] => {
      if (!Array.isArray(items) || items.length === 0) {
        return []
      }

      const normalizedSearch = search.trim().toLowerCase()
      const activeCategories = selectedCategories.map((slug) => slug.toLowerCase())

      const filtered = items.filter((item) => {
        if (activeCategories.length > 0) {
          const itemSlugs = collectCategorySlugs(item)
          if (mode === 'and') {
            const allMatch = activeCategories.every((slug) => itemSlugs.includes(slug))
            if (!allMatch) {
              return false
            }
          } else {
            const anyMatch = activeCategories.some((slug) => itemSlugs.includes(slug))
            if (!anyMatch) {
              return false
            }
          }
        }

        if (!normalizedSearch) {
          return true
        }

        const haystack = [
          item.headline,
          item.tease,
          item.short_story_preview,
          item.short_story_preferred,
          item.short_story_neutral,
        ]
          .filter((value) => typeof value === 'string' && value)
          .join(' ')
          .toLowerCase()

        return haystack.includes(normalizedSearch)
      })

      return sortItemsByIdOrder(filtered, orderOverride ?? myOrderedIds)
    },
    [mode, myOrderedIds, search, selectedCategories],
  )

  React.useEffect(() => {
    const sourceItems = Array.isArray(storeMyItems) ? [...storeMyItems] : []
    const sortedAll = sortItemsByIdOrder(sourceItems, myOrderedIds)
    const nextItems = filterMyItems(sortedAll)

    setMyItemsState((prev) => {
      if (itemsMatchOrder(sortedAll, prev.allItems) && itemsMatchOrder(nextItems, prev.items)) {
        return prev
      }
      return { ...prev, allItems: sortedAll, items: nextItems }
    })
  }, [filterMyItems, myOrderedIds, storeMyItems])

  const shouldHydrateMyOnly = React.useCallback((reason: 'mount' | 'focus') => {
    if (myHydrationInFlightRef.current) {
      return false
    }
    if (!myHydratedRef.current) {
      return true
    }

    const noItems = myAllItemsLengthRef.current === 0
    if (noItems) {
      if (!lastMyHydrateAtRef.current) {
        return true
      }
      if (reason === 'focus') {
        return Date.now() - lastMyHydrateAtRef.current > 5 * 60 * 1000
      }
      return false
    }

    if (reason === 'focus') {
      if (!lastMyHydrateAtRef.current) {
        return true
      }
      return Date.now() - lastMyHydrateAtRef.current > 5 * 60 * 1000
    }

    return false
  }, [])

  const hydrateMyOnly = React.useCallback(
    async (reason: 'mount' | 'focus') => {
      if (!myOnlyRef.current) {
        return
      }
      if (myHydrationInFlightRef.current) {
        return
      }

      myHydrationInFlightRef.current = true
      myHydratedRef.current = false
      setMyHydrated(false)

      const hadItems = myAllItemsLengthRef.current
      const { myCount: currentMyCount } = usePrepListStore.getState()

      setMyItemsState((prev) => ({ ...prev, loading: true, error: null }))
      usePrepListStore.setState((state) => ({ ...state, myIdsLoading: true }))

      try {
        const perPageRaw = usePrepListStore.getState().myPerPage
        const perPage = Number.isFinite(perPageRaw)
          ? Math.max(3, Math.min(15, Math.floor(perPageRaw)))
          : 10
        const response = await fetchMyPrepList({ page: 1, limit: perPage })
        const responseWithItems = response as MyPrepListItemsResponse
        const rawItems = Array.isArray(responseWithItems.items)
          ? (responseWithItems.items as PrepListItem[])
          : []

        const normalizedItems = rawItems.map((item) => ({ ...item, is_in_my_prep: true }))
        const orderedIds = Array.from(
          new Set(
            normalizedItems
              .map((item) =>
                typeof item.id === 'number' && Number.isFinite(item.id) && item.id > 0
                  ? Math.floor(item.id)
                  : null,
              )
              .filter((id): id is number => typeof id === 'number' && id > 0),
          ),
        )
        const orderedItems = sortItemsByIdOrder(normalizedItems, orderedIds)
        const normalizedPage =
          typeof response.page === 'number' && Number.isFinite(response.page)
            ? Math.max(1, Math.floor(response.page))
            : 1
        const normalizedPerPage =
          typeof response.per_page === 'number' && Number.isFinite(response.per_page)
            ? Math.max(3, Math.min(15, Math.floor(response.per_page)))
            : perPage
        const nextCount =
          typeof response.total === 'number' && Number.isFinite(response.total)
            ? Math.max(0, Math.floor(response.total))
            : resolveServerTotal(response, orderedItems)
        const nextHasMore = normalizedPage * normalizedPerPage < nextCount

        usePrepListStore.setState((state) => ({
          ...state,
          myIds: new Set(orderedIds),
          myOrderedIds: orderedIds,
          myItems: orderedItems,
          myIdsHydrated: true,
          myPage: normalizedPage,
          myPerPage: normalizedPerPage,
          myHasMore: nextHasMore,
          myCount: nextCount,
          myLoadingMore: false,
        }))

        const filterFn = filterMyItemsRef.current
        const filteredItems = filterFn ? filterFn(orderedItems, orderedIds) : orderedItems

        setMyItemsState({
          allItems: orderedItems,
          items: filteredItems,
          loading: false,
          error: null,
        })

        myAllItemsLengthRef.current = orderedItems.length
        myHydratedRef.current = true
        lastMyHydrateAtRef.current = Date.now()
        setMyHydrated(true)
      } catch (error) {
        const requestId = extractRequestId(error)
        const message =
          error && typeof error === 'object' && 'message' in error
            ? String((error as { message?: unknown }).message ?? 'Unable to load My Prep Sheet.')
            : 'Unable to load My Prep Sheet.'
        const displayMessage = withRequestId(message, requestId)
        setMyItemsState((prev) => ({ ...prev, loading: false, error: displayMessage }))
        setSnackbar({ message: displayMessage, severity: 'error' })
        lastMyHydrateAtRef.current = Date.now()
        myHydratedRef.current = true
        setMyHydrated(true)
      } finally {
        usePrepListStore.setState((state) => ({ ...state, myIdsLoading: false }))
        myHydrationInFlightRef.current = false
      }
    },
    [setSnackbar],
  )

  const shouldHydrateMyOnlyRef = React.useRef(shouldHydrateMyOnly)
  const hydrateMyOnlyRef = React.useRef(hydrateMyOnly)

  React.useEffect(() => {
    shouldHydrateMyOnlyRef.current = shouldHydrateMyOnly
  }, [shouldHydrateMyOnly])

  React.useEffect(() => {
    hydrateMyOnlyRef.current = hydrateMyOnly
  }, [hydrateMyOnly])

  const scheduleMyHydrationOnFocus = React.useCallback(() => {
    if (!myOnlyRef.current) {
      return
    }
    if (myHydrationInFlightRef.current) {
      return
    }
    if (!shouldHydrateMyOnlyRef.current('focus')) {
      return
    }

    if (typeof window === 'undefined') {
      void hydrateMyOnlyRef.current('focus')
      return
    }

    if (focusHydrateTimeoutRef.current !== null) {
      window.clearTimeout(focusHydrateTimeoutRef.current)
    }

    focusHydrateTimeoutRef.current = window.setTimeout(() => {
      focusHydrateTimeoutRef.current = null
      if (!myOnlyRef.current) {
        return
      }
      if (myHydrationInFlightRef.current) {
        return
      }
      if (!shouldHydrateMyOnlyRef.current('focus')) {
        return
      }
      void hydrateMyOnlyRef.current('focus')
    }, 1000)
  }, [])

  const scheduleMyHydrationOnFocusRef = React.useRef(scheduleMyHydrationOnFocus)
  React.useEffect(() => {
    scheduleMyHydrationOnFocusRef.current = scheduleMyHydrationOnFocus
  }, [scheduleMyHydrationOnFocus])

  React.useEffect(() => {
    if (!myOnly) {
      return
    }
    if (!shouldHydrateMyOnlyRef.current('mount')) {
      return
    }
    void hydrateMyOnlyRef.current('mount')
  }, [myOnly])

  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return
    }
    if (!myOnly) {
      return
    }

    const handleFocus = () => {
      scheduleMyHydrationOnFocusRef.current()
    }

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        scheduleMyHydrationOnFocusRef.current()
      }
    }

    window.addEventListener('focus', handleFocus)
    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      window.removeEventListener('focus', handleFocus)
      document.removeEventListener('visibilitychange', handleVisibility)
      if (focusHydrateTimeoutRef.current !== null) {
        window.clearTimeout(focusHydrateTimeoutRef.current)
        focusHydrateTimeoutRef.current = null
      }
    }
  }, [myOnly])

  React.useEffect(() => {
    if (!ariaLiveMessage) {
      return
    }
    const handle = window.setTimeout(() => setAriaLiveMessage(''), 1500)
    return () => {
      window.clearTimeout(handle)
    }
  }, [ariaLiveMessage])

  const commitReorder = React.useCallback(
    async (globalFrom: number, globalTo: number) => {
      markDashboardMyPrepDirty()
      try {
        const outcome = await usePrepListStore.getState().reorderMyPrep(globalFrom, globalTo)

        if (outcome === 'success') {
          setSnackbar({ message: 'Order saved', severity: 'success' })
        } else if (outcome === 'mismatch') {
          setSnackbar({
            message: 'Couldn’t save order because some items are hidden. We’ve reloaded your list.',
            severity: 'warning',
          })
        }
      } catch (error) {
        const requestId = extractRequestId(error)
        const message =
          error instanceof Error && typeof error.message === 'string' && error.message.trim() !== ''
            ? error.message
            : 'Couldn’t save order.'
        setSnackbar({ message: withRequestId(message, requestId), severity: 'error' })
      } finally {
        syncAuthoritativeMyPrepOrder(null, usePrepListStore.getState().myAllOrderedIds)
      }
    },
    [setSnackbar],
  )

  const handleDragStart = React.useCallback(
    (_event: DragStartEvent) => {
      if (!canReorder) {
        return
      }
    },
    [canReorder],
  )

  const handleDragCancel = React.useCallback(
    (_event: DragCancelEvent) => {
      if (!canReorder) {
        return
      }
      setAriaLiveMessage('')
    },
    [canReorder],
  )

  const handleDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (!canReorder) {
        return
      }
      if (!over) {
        return
      }
      const activeId = Number(active.id)
      const overId = Number(over.id)
      if (Number.isNaN(activeId) || Number.isNaN(overId) || activeId === overId) {
        return
      }
      const fromIndex = myOrderedIds.findIndex((id) => id === activeId)
      const toIndex = myOrderedIds.findIndex((id) => id === overId)
      if (fromIndex === -1 || toIndex === -1) {
        return
      }
      const storeState = usePrepListStore.getState()
      const pageStart = Math.max(0, (storeState.myPage - 1) * storeState.myPerPage)
      const globalFrom = storeState.myAllOrderedIds.indexOf(activeId)
      const globalTo = storeState.myAllOrderedIds.indexOf(overId)
      const resolvedFrom = globalFrom >= 0 ? globalFrom : pageStart + fromIndex
      const resolvedTo = globalTo >= 0 ? globalTo : pageStart + toIndex

      if (resolvedFrom === resolvedTo) {
        return
      }

      setAriaLiveMessage(`Moved to position ${toIndex + 1} of ${listAllMyItems.length}.`)
      void commitReorder(resolvedFrom, resolvedTo)
    },
    [canReorder, commitReorder, listAllMyItems.length, myOrderedIds],
  )

  const handleKeyboardReorder = React.useCallback(
    (id: number, direction: 'up' | 'down') => {
      if (!canReorder) {
        return
      }
      const currentIndex = myOrderedIds.findIndex((value) => value === id)
      if (currentIndex === -1) {
        return
      }
      const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1
      if (targetIndex < 0 || targetIndex >= myOrderedIds.length) {
        return
      }

      const storeState = usePrepListStore.getState()
      const pageStart = Math.max(0, (storeState.myPage - 1) * storeState.myPerPage)
      const globalFrom = storeState.myAllOrderedIds.indexOf(id)
      const targetId = myOrderedIds[targetIndex]
      const globalTo = typeof targetId === 'number' ? storeState.myAllOrderedIds.indexOf(targetId) : -1
      const resolvedFrom = globalFrom >= 0 ? globalFrom : pageStart + currentIndex
      const resolvedTo = globalTo >= 0 ? globalTo : pageStart + targetIndex

      if (resolvedFrom === resolvedTo) {
        return
      }

      setAriaLiveMessage(`Moved to position ${targetIndex + 1} of ${listAllMyItems.length}.`)
      void commitReorder(resolvedFrom, resolvedTo)
    },
    [canReorder, commitReorder, listAllMyItems.length, myOrderedIds],
  )

  const prepIdParam = searchParams.get('prepId')
  const selectedId = React.useMemo(() => {
    if (!prepIdParam) {
      return null
    }
    const parsed = Number(prepIdParam)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null
  }, [prepIdParam])

  const selectedItemIndex = React.useMemo(() => {
    if (selectedId === null) {
      return -1
    }
    return listItems.findIndex((entry) => entry.id === selectedId)
  }, [listItems, selectedId])

  const selectedItemInMy = React.useMemo(() => {
    if (selectedId === null) {
      return false
    }
    if (myIds.has(selectedId)) {
      return true
    }
    const match = listItems.find((entry) => entry.id === selectedId)
    return Boolean(match?.is_in_my_prep)
  }, [listItems, myIds, selectedId])

  React.useEffect(() => {
    setSelectedPrepIdInStore(selectedId, { replace: true })
  }, [selectedId, setSelectedPrepIdInStore])

  const splitContainerRef = React.useRef<HTMLDivElement | null>(null)
  const dragStateRef = React.useRef<{ startSplit: number; width: number; left: number } | null>(null)
  const dragCleanupRef = React.useRef<(() => void) | null>(null)
  const [isDraggingSplit, setIsDraggingSplit] = React.useState(false)
  const [singleDetailLoading, setSingleDetailLoading] = React.useState(false)
  const [singleDetailError, setSingleDetailError] = React.useState<string | null>(null)
  const categoryRequestRef = React.useRef<AbortController | null>(null)
  const myCategoryRequestRef = React.useRef<AbortController | null>(null)
  const listRequestRef = React.useRef<AbortController | null>(null)
  const detailCacheRef = React.useRef<Map<number, PrepDetailResponse>>(new Map())
  const retainedUsedDrawerIdRef = React.useRef<number | null>(null)
  const prefetchControllersRef = React.useRef<Map<number, AbortController>>(new Map())
  const lastScrollOffsetRef = React.useRef(0)
  const viewportScrollRef = React.useRef<HTMLDivElement | null>(null)
  const prefetchIndexRef = React.useRef(0)
  const mountedRef = React.useRef(false)
  const idlePrefetchHandleRef = React.useRef<number | null>(null)
  const lastMyOnlyIndexRef = React.useRef<number | null>(null)
  const lastMyHydrateAtRef = React.useRef<number | null>(null)
  const myAllItemsLengthRef = React.useRef(0)
  const myHydrationInFlightRef = React.useRef(false)
  const myHydratedRef = React.useRef(false)
  const modeHydratedRef = React.useRef(false)
  const modeSyncingRef = React.useRef<null | 'writer' | 'reader'>(null)
  const lastUrlWrittenRef = React.useRef<string | null>(null)
  const modeRef = React.useRef(mode)
  const searchParamsRef = React.useRef(searchParams)
  const focusHydrateTimeoutRef = React.useRef<number | null>(null)
  const myOnlyRef = React.useRef(myOnly)
  const filterMyItemsRef = React.useRef(filterMyItems)
  const ensuredMyIdsOnceRef = React.useRef(false)

  React.useEffect(() => {
    myAllItemsLengthRef.current = myItemsState.allItems.length
  }, [myItemsState.allItems.length])

  React.useEffect(() => {
    myHydratedRef.current = myHydrated
  }, [myHydrated])

  React.useEffect(() => {
    myOnlyRef.current = myOnly
  }, [myOnly])

  React.useEffect(() => {
    filterMyItemsRef.current = filterMyItems
  }, [filterMyItems])

  React.useEffect(() => {
    modeRef.current = mode
  }, [mode])

  React.useEffect(() => {
    searchParamsRef.current = searchParams
  }, [searchParams])

  React.useEffect(() => {
    const currentQuery = searchParams.toString()

    if (lastUrlWrittenRef.current && lastUrlWrittenRef.current === currentQuery) {
      lastUrlWrittenRef.current = null
      if (!modeHydratedRef.current) {
        modeHydratedRef.current = true
      }
      return
    }

    if (modeSyncingRef.current === 'writer') {
      return
    }

    if (!modeHydratedRef.current) {
      modeHydratedRef.current = true
    }

    const rawUrlMode = searchParams.get('mode')
    const normalizedUrlMode =
      rawUrlMode === 'single' || rawUrlMode === 'and' || rawUrlMode === 'or' ? rawUrlMode : undefined

    const currentMode = modeRef.current

    if (normalizedUrlMode && normalizedUrlMode !== currentMode) {
      modeSyncingRef.current = 'reader'
      setMode(normalizedUrlMode)
      queueMicrotask(() => {
        modeSyncingRef.current = null
      })
    }
  }, [searchParams, setMode])

  React.useEffect(() => {
    if (!modeHydratedRef.current || modeSyncingRef.current === 'reader') {
      return
    }

    const currentSearchParams = searchParamsRef.current
    const currentQuery = currentSearchParams.toString()
    const next = new URLSearchParams(currentSearchParams)

    if (mode === 'or') {
      next.delete('mode')
    } else {
      next.set('mode', mode)
    }

    const nextStr = next.toString()

    if (nextStr === currentQuery) {
      return
    }

    modeSyncingRef.current = 'writer'
    lastUrlWrittenRef.current = nextStr
    setSearchParams(next, { replace: true })
    queueMicrotask(() => {
      modeSyncingRef.current = null
    })
  }, [mode, setSearchParams])

  React.useEffect(() => {
    return () => {
      if (focusHydrateTimeoutRef.current !== null) {
        if (typeof window !== 'undefined') {
          window.clearTimeout(focusHydrateTimeoutRef.current)
        }
        focusHydrateTimeoutRef.current = null
      }
    }
  }, [])

  React.useEffect(() => {
    if (!myOnly) {
      return
    }
    if (myHydrationInFlightRef.current) {
      return
    }
    if (myItemsState.allItems.length > 0) {
      return
    }
    const snapshot = usePrepListStore.getState()
    if (!Array.isArray(snapshot.myItems) || snapshot.myItems.length === 0) {
      return
    }
    const ordered = sortItemsByIdOrder([...snapshot.myItems], snapshot.myOrderedIds)
    setMyItemsState({
      allItems: ordered,
      items: filterMyItems(ordered, snapshot.myOrderedIds),
      loading: false,
      error: null,
    })
  }, [filterMyItems, myItemsState.allItems.length, myOnly])
  const hasDevCheckedGridRef = React.useRef(false)
  const [singleScrollContainer, setSingleScrollContainer] = React.useState<HTMLDivElement | null>(null)
  const handleSingleScrollContainerRef = React.useCallback((node: HTMLDivElement | null) => {
    setSingleScrollContainer(node)
  }, [])

  React.useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  React.useEffect(() => {
    return () => {
      cancelSelectionTasks()
    }
  }, [cancelSelectionTasks])

  const handleCloseSnackbar = React.useCallback(
    (_event?: unknown, reason?: string) => {
      if (reason === 'clickaway') {
        return
      }
      setSnackbar((current) => {
        if (current?.action === 'undo-my-remove') {
          setPendingMyUndo(null)
        }
        if (current?.action === 'undo-used') {
          setUsedUndoState(null)
        }
        return null
      })
    },
    [setPendingMyUndo],
  )

  const exitSnapshotRef = React.useRef<PrepListPreviousView | null>(null)

  const getMyPrepEnterSelection = React.useCallback((): EnterMyOnlySelection => {
    if (myOnly) {
      return undefined
    }
    const selectedItemSource =
      selectedId != null ? (selectedItemInMy ? 'my' : 'all') : undefined

    return {
      selectedItemId: selectedId,
      selectedItemIndex: selectedItemIndex >= 0 ? selectedItemIndex : undefined,
      selectedItemSource,
      selectedWasOpen: Boolean(selectedId),
    }
  }, [myOnly, selectedId, selectedItemIndex, selectedItemInMy])

  const handleMyPrepToggleStart = React.useCallback(
    (nextEnabled: boolean) => {
      if (nextEnabled) {
        setMyItemsState((prev) => ({ ...prev, loading: true, error: null }))
        myHydratedRef.current = false
        setMyHydrated(false)
        return
      }

      exitSnapshotRef.current = usePrepListStore.getState().previousView ?? null
    },
    [setMyHydrated, setMyItemsState],
  )

  const handleMyPrepToggled = React.useCallback(
    (enabled: boolean) => {
      if (enabled) {
        const state = usePrepListStore.getState()
        const order = state.myOrderedIds
        const itemsFromStore = Array.isArray(state.myItems) ? [...state.myItems] : []
        const normalized = sortItemsByIdOrder(itemsFromStore, order)
        setMyItemsState({
          allItems: normalized,
          items: filterMyItems(normalized, order),
          loading: false,
          error: null,
        })
        lastMyHydrateAtRef.current = Date.now()
        myHydratedRef.current = true
        setMyHydrated(true)
        return
      }

      const snapshot = exitSnapshotRef.current
      exitSnapshotRef.current = null
      if (!snapshot) {
        return
      }
      const restoredId = snapshot.selectedItemId ?? null
      if (snapshot.selectedWasOpen === false) {
        setSelectedPrepId(null, { replace: true })
        return
      }
      setSelectedPrepId(restoredId, { replace: true })
    },
    [filterMyItems, setMyHydrated, setMyItemsState, setSelectedPrepId],
  )

  const handleMyPrepToggleError = React.useCallback(
    (error: unknown, nextEnabled: boolean) => {
      if (!nextEnabled) {
        exitSnapshotRef.current = null
        return
      }

      setMyItemsState((prev) => ({ ...prev, loading: false }))
      const requestId = extractRequestId(error)
      const message =
        error && typeof error === 'object' && 'message' in error
          ? String((error as { message?: unknown }).message ?? 'Unable to load My Prep Sheet.')
          : 'Unable to load My Prep Sheet.'
      setSnackbar({ message: withRequestId(message, requestId), severity: 'error' })
      myHydratedRef.current = true
      setMyHydrated(true)
    },
    [setMyHydrated, setMyItemsState, setSnackbar],
  )

  React.useEffect(() => {
    if (categoryRequestRef.current) {
      categoryRequestRef.current.abort()
    }
    if (categoryRetryTimerRef.current && typeof window !== 'undefined') {
      window.clearTimeout(categoryRetryTimerRef.current)
      categoryRetryTimerRef.current = null
    }

    const controller = new AbortController()
    categoryRequestRef.current = controller
    setCategoryRetryReady(false)
    setCategoryState((prev) => ({ ...prev, loading: true, error: null }))

    const trimmedSearch = search.trim()
    const searching = trimmedSearch.length > 0
    const categoryParams: {
      scope: 'global'
      q?: string
      ageMaxDays?: number
      dateBasis: typeof prepDateBasis
    } = {
      scope: 'global',
      dateBasis: prepDateBasis,
    }

    if (searching) {
      categoryParams.q = trimmedSearch
    }
    if (typeof prepAgeMaxDays === 'number' && prepAgeMaxDays > 0) {
      categoryParams.ageMaxDays = prepAgeMaxDays
    }

    const attemptCount = PREP_FETCH_RETRY_DELAYS_MS.length + 1

    const load = async () => {
      let finalError: unknown = null
      for (let attempt = 0; attempt < attemptCount; attempt += 1) {
        try {
          const response = await fetchPrepCategories(categoryParams, { signal: controller.signal })
          if (controller.signal.aborted) {
            return
          }

          const categories = Array.isArray(response.categories) ? response.categories : []
          lastGoodCategoriesRef.current = categories
          setCategoryState({ loading: false, items: categories, error: null })
          setCategoryRetryReady(false)
          if (categoryRequestRef.current === controller) {
            categoryRequestRef.current = null
          }
          return
        } catch (error) {
          if (controller.signal.aborted) {
            return
          }
          finalError = error
          const hasMore = attempt < attemptCount - 1
          if (!hasMore || !isRetriablePrepFetchError(error, { allow508: true })) {
            break
          }
          const delay = computeRetryDelay(PREP_FETCH_RETRY_DELAYS_MS[attempt] ?? 0)
          if (delay > 0) {
            await wait(delay)
          }
        }
      }

      const requestId = extractRequestId(finalError)
      const message =
        finalError && typeof finalError === 'object' && 'message' in finalError
          ? String((finalError as { message?: unknown }).message ?? 'Failed to load categories.')
          : 'Failed to load categories.'
      setCategoryState((prev) => ({
        loading: false,
        items: lastGoodCategoriesRef.current.length ? lastGoodCategoriesRef.current : prev.items,
        error: lastGoodCategoriesRef.current.length ? null : withRequestId(message, requestId),
      }))
      if (typeof window !== 'undefined') {
        categoryRetryTimerRef.current = window.setTimeout(() => {
          setCategoryRetryReady(true)
        }, 20000)
      }
      if (categoryRequestRef.current === controller) {
        categoryRequestRef.current = null
      }
    }

    void load()

    return () => {
      controller.abort()
    }
  }, [categoryReloadToken, prepAgeMaxDays, prepDateBasis, search])

  React.useEffect(() => {
    if (!myOnly) {
      if (myCategoryRequestRef.current) {
        myCategoryRequestRef.current.abort()
        myCategoryRequestRef.current = null
      }
      return
    }

    if (myCategoryRequestRef.current) {
      myCategoryRequestRef.current.abort()
    }

    const controller = new AbortController()
    myCategoryRequestRef.current = controller

    fetchPrepCategories({ scope: 'my', q: search.trim() || undefined }, { signal: controller.signal })
      .then((response) => {
        if (controller.signal.aborted) {
          return
        }

        const counts: Record<number, number> = {}

        if (Array.isArray(response.categories)) {
          for (const category of response.categories) {
            if (!category || typeof category.id !== 'number') {
              continue
            }
            counts[category.id] = typeof category.count === 'number' ? Math.max(0, category.count) : 0
          }
        }

        applyMyCategoryCounts(counts)
      })
      .catch((error) => {
        if (controller.signal.aborted) {
          return
        }
        applyMyCategoryCounts({})
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.error('[prep] Failed to load My Prep category counts', error)
        }
      })
      .finally(() => {
        if (myCategoryRequestRef.current === controller) {
          myCategoryRequestRef.current = null
        }
      })

    return () => {
      controller.abort()
    }
  }, [applyMyCategoryCounts, myOnly, search])

  const cancelIdlePrefetch = React.useCallback(() => {
    if (idlePrefetchHandleRef.current === null) {
      return
    }
    if (typeof window !== 'undefined') {
      if ('cancelIdleCallback' in window) {
        ;(window as typeof window & { cancelIdleCallback: (handle: number) => void }).cancelIdleCallback(
          idlePrefetchHandleRef.current,
        )
      } else {
        window.clearTimeout(idlePrefetchHandleRef.current)
      }
    }
    idlePrefetchHandleRef.current = null
  }, [])

  const abortPrefetches = React.useCallback(() => {
    cancelIdlePrefetch()
    prefetchControllersRef.current.forEach((controller) => controller.abort())
    prefetchControllersRef.current.clear()
  }, [cancelIdlePrefetch])

  React.useEffect(
    () => () => {
      abortPrefetches()
      if (categoryRequestRef.current) {
        categoryRequestRef.current.abort()
      }
      if (listRequestRef.current) {
        listRequestRef.current.abort()
      }
      if (categoryRetryTimerRef.current && typeof window !== 'undefined') {
        window.clearTimeout(categoryRetryTimerRef.current)
      }
    },
    [abortPrefetches],
  )

  const loadDetail = React.useCallback(
    async (id: number, signal?: AbortSignal): Promise<PrepDetailResponse> => {
      const cached = detailCacheRef.current.get(id)
      if (cached) {
        return cached
      }
      const detail = await fetchPrepItemDetail(id, signal ? { signal } : {})
      detailCacheRef.current.set(id, detail)
      return detail
    },
    [],
  )

  const prefetchDetail = React.useCallback(
    (id: number) => {
      if (detailCacheRef.current.has(id) || prefetchControllersRef.current.has(id)) {
        return
      }
      const controller = new AbortController()
      prefetchControllersRef.current.set(id, controller)
      fetchPrepItemDetail(id, { signal: controller.signal })
        .then((detail) => {
          detailCacheRef.current.set(id, detail)
        })
        .catch(() => {
          /* ignore prefetch errors */
        })
        .finally(() => {
          prefetchControllersRef.current.delete(id)
        })
    },
    [],
  )

  const updateDetailCache = React.useCallback((id: number, updater: (detail: PrepDetailResponse) => PrepDetailResponse) => {
    const existing = detailCacheRef.current.get(id)
    if (!existing) {
      return
    }
    detailCacheRef.current.set(id, updater(existing))
  }, [])

  const fetchItems = React.useCallback(
    (append: boolean, cursorOverride?: string | null) => {
      if (listRequestRef.current) {
        listRequestRef.current.abort()
      }

      const controller = new AbortController()
      listRequestRef.current = controller

      if (!append) {
        prefetchIndexRef.current = 0
        cancelIdlePrefetch()
      }

      setItemsState((prev) => ({
        ...prev,
        loading: append ? prev.loading : true,
        loadingMore: append,
        error: null,
        items: append ? prev.items : [],
      }))

      const trimmedSearch = search.trim()
      const searching = trimmedSearch.length > 0
      const params: PrepSearchParams = {
        scope: 'global',
        categories: selectedCategories,
        logic: mode === 'and' ? 'and' : 'or',
        limit: PAGE_SIZE,
        date_basis: prepDateBasis,
      }

      if (append) {
        params.cursor = cursorOverride ?? undefined
      }

      if (searching) {
        params.q = trimmedSearch
        if (searchIncludeDismissed) {
          params.include_dismissed = true
        }
      }
      if (typeof prepAgeMaxDays === 'number' && prepAgeMaxDays > 0) {
        params.age_max_days = prepAgeMaxDays
      }

      searchPrepItems(params, { signal: controller.signal })
        .then((response) => {
          setItemsState((prev) => ({
            items: append ? [...prev.items, ...response.items] : response.items,
            loading: false,
            loadingMore: false,
            hasMore: Boolean(response.next_cursor),
            cursor: response.next_cursor ?? null,
            error: null,
          }))
          setCursorInStore(response.next_cursor ?? null)
          syncMyIdsFromItems(response.items)
        })
        .catch((error) => {
          if (controller.signal.aborted) {
            return
          }
          const requestId = extractRequestId(error)
          const message =
            error && typeof error === 'object' && 'message' in error
              ? String((error as { message?: unknown }).message ?? 'Failed to load prep items.')
              : 'Failed to load prep items.'
          const displayMessage = withRequestId(message, requestId)
          setItemsState((prev) => ({
            ...prev,
            loading: false,
            loadingMore: false,
            error: displayMessage,
          }))
          setSnackbar({ message: displayMessage, severity: 'error' })
        })
        .finally(() => {
          if (listRequestRef.current === controller) {
            listRequestRef.current = null
          }
        })
    },
    [
      cancelIdlePrefetch,
      mode,
      prepAgeMaxDays,
      prepDateBasis,
      search,
      searchIncludeDismissed,
      selectedCategories,
      setCursorInStore,
      syncMyIdsFromItems,
    ],
  )

  React.useEffect(() => {
    if (!ensuredMyIdsOnceRef.current) {
      ensuredMyIdsOnceRef.current = true
      void ensureFullMyIds().catch(() => undefined)
    }
    if (myOnly) {
      return
    }
    fetchItems(false, null)
  }, [ensureFullMyIds, fetchItems, myOnly])

  const loadMore = React.useCallback(() => {
    if (myOnly) {
      return
    }
    setItemsState((prev) => {
      if (!prev.hasMore || prev.loadingMore || prev.loading) {
        return prev
      }
      fetchItems(true, prev.cursor)
      return prev
    })
  }, [fetchItems, myOnly])

  React.useEffect(() => {
    const items = listItems
    prefetchIndexRef.current = Math.min(prefetchIndexRef.current, items.length)
    if (!items.length) {
      cancelIdlePrefetch()
      return
    }

    const schedule = (cb: () => void): number | null => {
      if (typeof window === 'undefined') {
        cb()
        return null
      }
      if ('requestIdleCallback' in window) {
        return (window as typeof window & { requestIdleCallback: (callback: IdleRequestCallback) => number }).requestIdleCallback(
          () => cb(),
        )
      }
      return window.setTimeout(cb, 200)
    }

    const run = () => {
      const start = prefetchIndexRef.current
      const end = Math.min(items.length, start + IDLE_PREFETCH_BATCH)
      for (let index = start; index < end; index += 1) {
        const item = items[index]
        if (item) {
          prefetchDetail(item.id)
        }
      }
      prefetchIndexRef.current = end
      if (prefetchIndexRef.current < items.length) {
        idlePrefetchHandleRef.current = schedule(run)
      } else {
        idlePrefetchHandleRef.current = null
      }
    }

    cancelIdlePrefetch()
    idlePrefetchHandleRef.current = schedule(run)

    return () => {
      cancelIdlePrefetch()
    }
  }, [cancelIdlePrefetch, listItems, prefetchDetail])

  React.useEffect(() => {
    const viewportElement = viewportScrollRef.current
    if (viewportElement) {
      viewportElement.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [scrollResetToken, selectedCategories, search, mode])

  const handleUndoDismiss = React.useCallback(async () => {
    if (!undoState) {
      return
    }

    const { item: dismissedItem, index, wasInMyPrep, myPreviousIndex, myPreviousOrder, itemId } = undoState
    const resolvedItemId = itemId ?? dismissedItem.id

    setUndoState(null)

    setItemsState((prev) => {
      if (prev.items.some((existing) => existing.id === dismissedItem.id)) {
        return prev
      }
      const nextItems = [...prev.items]
      const insertIndex = Math.min(index, nextItems.length)
      nextItems.splice(insertIndex, 0, dismissedItem)
      return { ...prev, items: nextItems }
    })

    try {
      await undoDismissPrepItem(dismissedItem.id)
    } catch (error) {
      const requestId = extractRequestId(error)
      setSnackbar({ message: withRequestId('Unable to restore prep item.', requestId), severity: 'error' })
      return
    }

    if (!wasInMyPrep) {
      setSnackbar({ message: 'Restored to your feed.', severity: 'success' })
      return
    }

    const prepStoreState = usePrepListStore.getState()
    const canonicalOrder = Array.isArray(prepStoreState.myAllOrderedIds)
      ? [...prepStoreState.myAllOrderedIds]
      : []
    const snapshotOrder = Array.isArray(myPreviousOrder) ? [...myPreviousOrder] : []
    const combined = [...snapshotOrder, ...canonicalOrder]
    const uniqueCombined: number[] = []
    const seen = new Set<number>()
    for (const id of combined) {
      if (seen.has(id)) {
        continue
      }
      seen.add(id)
      uniqueCombined.push(id)
    }
    const withoutItem = uniqueCombined.filter((id) => id !== resolvedItemId)
    const insertAt = Math.max(0, Math.min(withoutItem.length, myPreviousIndex ?? withoutItem.length))
    withoutItem.splice(insertAt, 0, resolvedItemId)
    const targetOrder = withoutItem
    const restoredItem: PrepListItem = { ...dismissedItem, is_in_my_prep: true }

    try {
      await addPrepItemToMyPrep(dismissedItem.id)
    } catch (error) {
      const requestId = extractRequestId(error)
      setSnackbar({
        message: withRequestId('Restored, but My Prep order not updated.', requestId),
        severity: 'warning',
      })
      return
    }

    let reorderError: unknown = null
    try {
      if (targetOrder.length > 0) {
        await reorderMyPrepApi(targetOrder)
      }
    } catch (error) {
      reorderError = error
    }

    incMyCount()
    incMyCategoryCounts(restoredItem)

    setItemsState((prev) => ({
      ...prev,
      items: prev.items.map((existing) =>
        existing.id === dismissedItem.id ? { ...existing, is_in_my_prep: true } : existing,
      ),
    }))

    updateMyItemInStore(restoredItem, true)
    const { setMyOrderedIds } = usePrepListStore.getState()
    setMyOrderedIds(targetOrder)

    setMyItemsState((prev) => {
      const existingIndex = prev.allItems.findIndex((existing) => existing.id === dismissedItem.id)
      const base =
        existingIndex === -1
          ? [...prev.allItems, restoredItem]
          : prev.allItems.map((existing) =>
              existing.id === dismissedItem.id ? restoredItem : existing,
            )
      const ordered = sortItemsByIdOrder(base, targetOrder)
      return { ...prev, allItems: ordered, items: filterMyItems(ordered) }
    })

    updateDetailCache(dismissedItem.id, (prev) => ({ ...prev, is_in_my_prep: true }))

    if (reorderError) {
      const requestId = extractRequestId(reorderError)
      setSnackbar({
        message: withRequestId('Restored, but My Prep order not updated.', requestId),
        severity: 'warning',
      })
      return
    }

    setSnackbar({ message: 'Restored to your feed and My Prep.', severity: 'success' })
  }, [
    filterMyItems,
    incMyCategoryCounts,
    incMyCount,
    setItemsState,
    setMyItemsState,
    setSnackbar,
    undoState,
    updateDetailCache,
    updateMyItemInStore,
  ])

  const handleUndoMyRemove = React.useCallback(async () => {
    const snapshot = usePrepListStore.getState().pendingMyUndo
    if (!snapshot) {
      return
    }

    const restoredOrder = snapshot.previousOrder.includes(snapshot.itemId)
      ? [...snapshot.previousOrder]
      : [...snapshot.previousOrder, snapshot.itemId]
    const restoredItem: PrepListItem = { ...snapshot.item, is_in_my_prep: true }

    setPendingMyUndo(null)
    setSnackbar(null)

    let undoError: unknown = null
    try {
      await addPrepItemToMyPrep(snapshot.itemId)
      await reorderMyPrepApi(restoredOrder)
    } catch (error) {
      undoError = error
    }

    applyMyUndoRestore({ ...snapshot, previousOrder: restoredOrder })

    setItemsState((prev) => ({
      ...prev,
      items: prev.items.map((existing) =>
        existing.id === snapshot.itemId ? { ...existing, is_in_my_prep: true } : existing,
      ),
    }))

    setMyItemsState((prev) => {
      const base = prev.allItems.some((existing) => existing.id === snapshot.itemId)
        ? prev.allItems.map((existing) =>
            existing.id === snapshot.itemId ? restoredItem : existing,
          )
        : [...prev.allItems, restoredItem]
      const ordered = sortItemsByIdOrder(base, restoredOrder)
      return { ...prev, allItems: ordered, items: filterMyItems(ordered) }
    })

    updateDetailCache(snapshot.itemId, (prev) => ({ ...prev, is_in_my_prep: true }))
    incMyCount()
    incMyCategoryCounts(restoredItem)
    setSelectedPrepId(snapshot.itemId, { replace: true })

    if (undoError) {
      const requestId = extractRequestId(undoError)
      setSnackbar({
        message: withRequestId('Restored locally; sync failed.', requestId),
        severity: 'error',
      })
      return
    }

    setSnackbar({ message: 'Restored to My Prep', severity: 'success' })
  }, [
    applyMyUndoRestore,
    filterMyItems,
    incMyCategoryCounts,
    incMyCount,
    setItemsState,
    setMyItemsState,
    setPendingMyUndo,
    setSelectedPrepId,
    setSnackbar,
    updateDetailCache,
  ])

  const handleToggleMyPrep = React.useCallback(
    async (item: PrepListItem, next: boolean) => {
      const wasInMyPrep = Boolean(item.is_in_my_prep || myIds.has(item.id))

      if (next === wasInMyPrep) {
        return
      }

      if (next) {
        if (pendingMyUndo?.itemId === item.id) {
          setPendingMyUndo(null)
        }

        incMyCount()
        incMyCategoryCounts(item)

        setItemsState((prev) => ({
          ...prev,
          items: prev.items.map((existing) =>
            existing.id === item.id ? { ...existing, is_in_my_prep: true } : existing,
          ),
        }))
        const storeSnapshot = usePrepListStore.getState()
        const previousOrder = Array.isArray(storeSnapshot.myAllOrderedIds)
          ? [...storeSnapshot.myAllOrderedIds]
          : []
        const targetOrder = [...previousOrder.filter((id) => id !== item.id), item.id]

        markDashboardMyPrepDirty()
        updateMyItemInStore(item, true)
        storeSnapshot.setMyOrderedIds(targetOrder)
        usePrepDashboardStore.getState().patchMyPrepMembership(item.id, true, item, targetOrder)
        setMyItemsState((prev) => {
          const existingIndex = prev.allItems.findIndex((existing) => existing.id === item.id)
          const updatedItem: PrepListItem = { ...item, is_in_my_prep: true }
          const base =
            existingIndex === -1
              ? [...prev.allItems, updatedItem]
              : prev.allItems.map((existing) =>
                  existing.id === item.id ? { ...existing, is_in_my_prep: true } : existing,
                )
          const ordered = sortItemsByIdOrder(base, targetOrder)
          return { ...prev, allItems: ordered, items: filterMyItems(ordered) }
        })
        updateDetailCache(item.id, (prev) => ({ ...prev, is_in_my_prep: true }))

        try {
          const response = await addPrepItemToMyPrep(item.id)
          syncAuthoritativeMyPrepOrder(response, targetOrder)
        } catch (error) {
          setItemsState((prev) => ({
            ...prev,
            items: prev.items.map((existing) =>
              existing.id === item.id ? { ...existing, is_in_my_prep: false } : existing,
            ),
          }))
          updateDetailCache(item.id, (prev) => ({ ...prev, is_in_my_prep: false }))
          updateMyItemInStore(item, false)
          syncAuthoritativeMyPrepOrder(null, previousOrder)
          const rollbackOrder = previousOrder
          setMyItemsState((prev) => {
            const filtered = prev.allItems.filter((existing) => existing.id !== item.id)
            const ordered = sortItemsByIdOrder(filtered, rollbackOrder)
            return { ...prev, allItems: ordered, items: filterMyItems(ordered) }
          })
          decMyCount()
          decMyCategoryCounts(item)
          const requestId = extractRequestId(error)
          setSnackbar({
            message: withRequestId('Unable to update My Prep.', requestId),
            severity: 'error',
          })
          return
        }

        setSnackbar({ message: 'Added to My Prep', severity: 'success' })

        return
      }

      if (!wasInMyPrep) {
        return
      }

      decMyCount()
      decMyCategoryCounts(item)

      markDashboardMyPrepDirty()
      const removalSnapshot = applyMyRemovalOptimistic(item.id)
      usePrepListStore.getState().bumpMyMembershipVersion()
      const optimisticOrder = [...usePrepListStore.getState().myAllOrderedIds]
      usePrepDashboardStore.getState().patchMyPrepMembership(item.id, false, item, optimisticOrder)
      const snapshotItem: PrepListItem = removalSnapshot.removedItem
        ? { ...removalSnapshot.removedItem, is_in_my_prep: true }
        : { ...item, is_in_my_prep: true }
      const normalizedSnapshot = {
        itemId: item.id,
        item: snapshotItem,
        previousIndex: removalSnapshot.previousIndex,
        previousOrder: removalSnapshot.previousOrder,
        createdAt: Date.now(),
      }

      setItemsState((prev) => ({
        ...prev,
        items: prev.items.map((existing) =>
          existing.id === item.id ? { ...existing, is_in_my_prep: false } : existing,
        ),
      }))

      const orderAfterRemoval = usePrepListStore.getState().myOrderedIds
      setMyItemsState((prev) => {
        const nextAll = prev.allItems.filter((existing) => existing.id !== item.id)
        const ordered = sortItemsByIdOrder(nextAll, orderAfterRemoval)
        return { ...prev, allItems: ordered, items: filterMyItems(ordered) }
      })
      updateDetailCache(item.id, (prev) => ({ ...prev, is_in_my_prep: false }))

      const wasSelected = selectedId === item.id
      if (wasSelected) {
        setSelectedPrepId(null, { replace: true })
      }

      try {
        const response = await removePrepItemFromMyPrep(item.id)
        syncAuthoritativeMyPrepOrder(response, optimisticOrder)
        setPendingMyUndo(normalizedSnapshot)
        setSnackbar({ message: 'Removed from My Prep', severity: 'success', action: 'undo-my-remove' })
      } catch (error) {
        applyMyUndoRestore(normalizedSnapshot)
        setPendingMyUndo(null)
        syncAuthoritativeMyPrepOrder(null, normalizedSnapshot.previousOrder)

        setItemsState((prev) => ({
          ...prev,
          items: prev.items.map((existing) =>
            existing.id === item.id ? { ...existing, is_in_my_prep: true } : existing,
          ),
        }))

        const restoredOrder = normalizedSnapshot.previousOrder.includes(item.id)
          ? [...normalizedSnapshot.previousOrder]
          : [...normalizedSnapshot.previousOrder, item.id]

        setMyItemsState((prev) => {
          const base = prev.allItems.some((existing) => existing.id === item.id)
            ? prev.allItems.map((existing) =>
                existing.id === item.id ? snapshotItem : existing,
              )
            : [...prev.allItems, snapshotItem]
          const ordered = sortItemsByIdOrder(base, restoredOrder)
          return { ...prev, allItems: ordered, items: filterMyItems(ordered) }
        })

        updateDetailCache(item.id, (prev) => ({ ...prev, is_in_my_prep: true }))
        incMyCount()
        incMyCategoryCounts(item)
        if (wasSelected) {
          setSelectedPrepId(item.id, { replace: true })
        }
        usePrepListStore.getState().bumpMyMembershipVersion()
        const requestId = extractRequestId(error)
        setSnackbar({
          message: withRequestId('Unable to update My Prep.', requestId),
          severity: 'error',
        })
      }
    },
    [
      applyMyRemovalOptimistic,
      applyMyUndoRestore,
      decMyCategoryCounts,
      decMyCount,
      filterMyItems,
      incMyCategoryCounts,
      incMyCount,
      myIds,
      pendingMyUndo,
      selectedId,
      setItemsState,
      setMyItemsState,
      setPendingMyUndo,
      setSelectedPrepId,
      setSnackbar,
      updateDetailCache,
      updateMyItemInStore,
    ],
  )

  const handleDismiss = React.useCallback(
    async (item: PrepListItem) => {
      const previousItems = listItems
      const removalIndex = previousItems.findIndex((existing) => existing.id === item.id)

      if (removalIndex === -1) {
        return
      }

      const removalVisibleIndex = previousItems.findIndex((existing) => existing.id === item.id)
      const previousSelectedId = selectedId
      const prepStoreSnapshot = usePrepListStore.getState()
      const wasInMyPrep = Boolean(item.is_in_my_prep || prepStoreSnapshot.myIds.has(item.id))
      const myPreviousIndex = wasInMyPrep
        ? prepStoreSnapshot.myItems.findIndex((existing) => existing.id === item.id)
        : undefined
      const myPreviousOrder = wasInMyPrep ? [...prepStoreSnapshot.myAllOrderedIds] : undefined
      const undoItem: PrepListItem = wasInMyPrep ? { ...item, is_in_my_prep: false } : { ...item }

      let shouldLoadMore = false
      let loadMoreCursor: string | null = null

      if (myOnly || wasInMyPrep) {
        setMyItemsState((prev) => {
          const allIndex = prev.allItems.findIndex((existing) => existing.id === item.id)
          if (allIndex === -1) {
            return prev
          }
          const nextAll = [...prev.allItems]
          nextAll.splice(allIndex, 1)
          return { ...prev, allItems: nextAll, items: filterMyItems(nextAll) }
        })
      }

      setItemsState((prev) => {
        const index = prev.items.findIndex((existing) => existing.id === item.id)
        if (index === -1) {
          return prev
        }
        const nextItems = [...prev.items]
        nextItems.splice(index, 1)

        if (nextItems.length === 0 && prev.hasMore && !prev.loading && !prev.loadingMore) {
          shouldLoadMore = true
          loadMoreCursor = prev.cursor ?? null
        }

        return { ...prev, items: nextItems }
      })

      if (wasInMyPrep) {
        markDashboardMyPrepDirty()
        decMyCount()
        decMyCategoryCounts(item)
        updateMyItemInStore(item, false)
        updateDetailCache(item.id, (prev) => ({ ...prev, is_in_my_prep: false }))
      }

      const nextSelection = selectNextAfterRemoval({
        selectedId,
        removedId: item.id,
        visibleItems: previousItems,
        removalIndex: removalVisibleIndex,
        lastKnownIndex: myOnly ? lastMyOnlyIndexRef.current : undefined,
      })

      if (nextSelection !== undefined) {
        setSelectedPrepId(nextSelection, { replace: true })
      }

      const dismissSnapshot: DismissUndoSnapshot = {
        itemId: item.id,
        item: undoItem,
        index: removalIndex,
        createdAt: Date.now(),
        wasInMyPrep,
        myPreviousIndex,
        myPreviousOrder,
      }

      setUndoState(dismissSnapshot)

      try {
        await dismissPrepItem(item.id)

        if (shouldLoadMore) {
          fetchItems(true, loadMoreCursor)
        }

        if (wasInMyPrep) {
          void removePrepItemFromMyPrep(item.id).catch((error) => {
            if (process.env.NODE_ENV !== 'production') {
              console.error('[prep] Failed to sync My Prep removal after dismiss', error)
            }
          })
        }

        setSnackbar({ message: 'Dismissed for you.', severity: 'success', action: 'undo-dismiss' })
      } catch (error) {
        setItemsState((prev) => {
          const nextItems = [...prev.items]
          const insertIndex = Math.min(removalIndex, nextItems.length)
          nextItems.splice(insertIndex, 0, item)
          return { ...prev, items: nextItems }
        })

        if (wasInMyPrep || myOnly) {
          const restoredOrder = usePrepListStore.getState().myOrderedIds
          setMyItemsState((prev) => {
            const hasExisting = prev.allItems.some((existing) => existing.id === item.id)
            let nextAll: PrepListItem[]
            if (hasExisting) {
              nextAll = prev.allItems.map((existing) =>
                existing.id === item.id ? { ...existing, is_in_my_prep: wasInMyPrep } : existing,
              )
            } else {
              nextAll = [...prev.allItems]
              const insertAt = Math.min(removalIndex, nextAll.length)
              nextAll.splice(insertAt, 0, { ...item, is_in_my_prep: wasInMyPrep })
            }
            const ordered = sortItemsByIdOrder(nextAll, restoredOrder)
            return { ...prev, allItems: ordered, items: filterMyItems(ordered) }
          })
        }

        if (nextSelection !== undefined) {
          setSelectedPrepId(previousSelectedId, { replace: true })
        }

        if (wasInMyPrep) {
          incMyCount()
          incMyCategoryCounts(item)
          updateMyItemInStore(item, true)
          syncAuthoritativeMyPrepOrder(null, myPreviousOrder ?? [])
          updateDetailCache(item.id, (prev) => ({ ...prev, is_in_my_prep: true }))
        }

        setUndoState(null)
        const requestId = extractRequestId(error)
        setSnackbar({
          message: withRequestId('Unable to dismiss prep item.', requestId),
          severity: 'error',
        })
      }
    },
    [
      decMyCategoryCounts,
      decMyCount,
      dismissPrepItem,
      fetchItems,
      filterMyItems,
      incMyCategoryCounts,
      incMyCount,
      listItems,
      myIds,
      myOnly,
      removePrepItemFromMyPrep,
      selectedId,
      setSelectedPrepId,
      updateDetailCache,
      updateMyItemInStore,
    ],
  )

  const handleArchive = React.useCallback(
    async (item: PrepListItem) => {
      let removedIndex = -1
      setItemsState((prev) => {
        const index = prev.items.findIndex((existing) => existing.id === item.id)
        if (index === -1) {
          removedIndex = -1
          return prev
        }
        removedIndex = index
        const nextItems = [...prev.items]
        nextItems.splice(index, 1)
        return { ...prev, items: nextItems }
      })

      if (removedIndex === -1) {
        return
      }

      try {
        await archivePrepItem(item.id)
        setSnackbar({ message: 'Archived prep item for everyone', severity: 'success' })
      } catch (error) {
        setItemsState((prev) => {
          const nextItems = [...prev.items]
          const insertIndex = Math.min(removedIndex, nextItems.length)
          nextItems.splice(insertIndex, 0, item)
          return { ...prev, items: nextItems }
        })
        const requestId = extractRequestId(error)
        setSnackbar({
          message: withRequestId('Unable to archive prep item.', requestId),
          severity: 'error',
        })
      }
    },
    [],
  )

  const handleSelectPrepItem = React.useCallback(
    (id: number, options?: { replace?: boolean }) => {
      retainedUsedDrawerIdRef.current = null
      lastClickedIdRef.current = id
      setSelectedPrepId(id, options)
    },
    [setSelectedPrepId],
  )

  const closeDetail = React.useCallback(() => {
    retainedUsedDrawerIdRef.current = null
    lastClickedIdRef.current = null
    setSelectedPrepId(null, { replace: true })
  }, [setSelectedPrepId])

  const selectedListItem = React.useMemo(() => {
    if (!selectedId) {
      return null
    }
    return listItems.find((item) => item.id === selectedId) ?? null
  }, [listItems, selectedId])
  const myPrepNavIds = React.useMemo(() => {
    if (!myOnly) {
      return []
    }
    return listItems.map((item) => item.id)
  }, [listItems, myOnly])

  const cachedDetail = selectedId ? detailCacheRef.current.get(selectedId) ?? null : null
  const selectedDetail = cachedDetail
  const detailItem = selectedDetail ?? selectedListItem

  const handleMarkUsedFromDetail = React.useCallback(async () => {
    if (!selectedId || !stationId) {
      throw new Error('Unable to mark this item as used.')
    }

    const item = (
      itemsState.items.find((entry) => entry.id === selectedId) ??
      myItemsState.allItems.find((entry) => entry.id === selectedId) ??
      detailCacheRef.current.get(selectedId) ??
      null
    ) as unknown as PrepListItem | null

    if (!item) {
      throw new Error('Unable to find the selected prep item.')
    }

    const itemsIndex = itemsState.items.findIndex((entry) => entry.id === item.id)
    const myItemsIndex = myItemsState.allItems.findIndex((entry) => entry.id === item.id)
    retainedUsedDrawerIdRef.current = item.id

    if (!detailCacheRef.current.has(item.id)) {
      detailCacheRef.current.set(item.id, item as PrepDetailResponse)
    }
    updateDetailCache(item.id, (current) => ({ ...current, is_in_my_prep: false }))

    const mutation = beginPrepUsedOptimistic({
      item,
      userId: me.user.id,
      userName: me.user.name,
    })

    setItemsState((current) => ({
      ...current,
      items: current.items.filter((entry) => entry.id !== item.id),
    }))
    setMyItemsState((current) => {
      const nextAll = current.allItems.filter((entry) => entry.id !== item.id)
      return { ...current, allItems: nextAll, items: filterMyItems(nextAll) }
    })

    if (!mutation.owner) {
      return
    }

    if (mutation.wasInMyPrep) {
      decMyCategoryCounts(item)
    }

    const snapshot: PrepListUsedUndoSnapshot = { mutation, itemsIndex, myItemsIndex }

    try {
      const response = await markUsed(stationId, item.id)
      if (!response) {
        throw new Error('Unable to mark this item as used.')
      }
      if (!commitPrepUsed(mutation, response)) {
        return
      }
      setUsedUndoState(snapshot)
      setSnackbar({ message: 'Marked as used.', severity: 'success', action: 'undo-used' })
    } catch (error) {
      if (!rollbackPrepUsed(mutation)) {
        return
      }
      if (itemsIndex >= 0) {
        setItemsState((current) => {
          if (current.items.some((entry) => entry.id === item.id)) {
            return current
          }
          const nextItems = [...current.items]
          nextItems.splice(Math.min(itemsIndex, nextItems.length), 0, item)
          return { ...current, items: nextItems }
        })
      }
      if (mutation.wasInMyPrep && myItemsIndex >= 0) {
        setMyItemsState((current) => {
          if (current.allItems.some((entry) => entry.id === item.id)) {
            return current
          }
          const nextAll = [...current.allItems]
          nextAll.splice(Math.min(myItemsIndex, nextAll.length), 0, { ...item, is_in_my_prep: true })
          return { ...current, allItems: nextAll, items: filterMyItems(nextAll) }
        })
        incMyCategoryCounts(item)
      }
      updateDetailCache(item.id, (current) => ({
        ...current,
        is_in_my_prep: mutation.wasInMyPrep,
      }))
      retainedUsedDrawerIdRef.current = null
      const requestId = extractRequestId(error)
      setSnackbar({
        message: withRequestId('Unable to mark this item as used.', requestId),
        severity: 'error',
      })
      throw error
    }
  }, [
    decMyCategoryCounts,
    filterMyItems,
    incMyCategoryCounts,
    itemsState.items,
    markUsed,
    me.user.id,
    me.user.name,
    myItemsState.allItems,
    selectedId,
    stationId,
    updateDetailCache,
  ])

  const handleUndoUsed = React.useCallback(async () => {
    if (!usedUndoState || !stationId) {
      return
    }

    const snapshot = usedUndoState
    const { item } = snapshot.mutation
    setUsedUndoState(null)
    setSnackbar(null)
    beginPrepUsedUndoOptimistic(snapshot.mutation)

    if (snapshot.itemsIndex >= 0) {
      setItemsState((current) => {
        if (current.items.some((entry) => entry.id === item.id)) {
          return current
        }
        const nextItems = [...current.items]
        nextItems.splice(Math.min(snapshot.itemsIndex, nextItems.length), 0, item)
        return { ...current, items: nextItems }
      })
    }
    if (snapshot.mutation.wasInMyPrep && snapshot.myItemsIndex >= 0) {
      setMyItemsState((current) => {
        if (current.allItems.some((entry) => entry.id === item.id)) {
          return current
        }
        const nextAll = [...current.allItems]
        nextAll.splice(
          Math.min(snapshot.myItemsIndex, nextAll.length),
          0,
          { ...item, is_in_my_prep: true },
        )
        return { ...current, allItems: nextAll, items: filterMyItems(nextAll) }
      })
      incMyCategoryCounts(item)
    }
    updateDetailCache(item.id, (current) => ({
      ...current,
      is_in_my_prep: snapshot.mutation.wasInMyPrep,
    }))
    retainedUsedDrawerIdRef.current = null

    try {
      const response = await undoUsed(
        stationId,
        item.id,
        undefined,
        snapshot.mutation.wasInMyPrep
          ? { myPrep: true, position: snapshot.mutation.previousMyPrepPosition }
          : { myPrep: false, position: 0 },
      )
      if (!response?.undone) {
        throw new Error('Unable to undo the used flag.')
      }
      if (!commitPrepUsedUndo(snapshot.mutation, response)) {
        return
      }
      setSnackbar({ message: 'Marked as unused and restored.', severity: 'success' })
    } catch (error) {
      if (!rollbackPrepUsedUndo(snapshot.mutation)) {
        return
      }
      setItemsState((current) => ({
        ...current,
        items: current.items.filter((entry) => entry.id !== item.id),
      }))
      setMyItemsState((current) => {
        const nextAll = current.allItems.filter((entry) => entry.id !== item.id)
        return { ...current, allItems: nextAll, items: filterMyItems(nextAll) }
      })
      if (snapshot.mutation.wasInMyPrep) {
        decMyCategoryCounts(item)
      }
      updateDetailCache(item.id, (current) => ({ ...current, is_in_my_prep: false }))
      retainedUsedDrawerIdRef.current = item.id
      setUsedUndoState(snapshot)
      const requestId = extractRequestId(error)
      setSnackbar({
        message: withRequestId('Unable to undo the used flag.', requestId),
        severity: 'error',
        action: 'undo-used',
      })
    }
  }, [
    decMyCategoryCounts,
    filterMyItems,
    incMyCategoryCounts,
    stationId,
    undoUsed,
    updateDetailCache,
    usedUndoState,
  ])

  const handleToggleMyPrepFromDetail = React.useCallback(
    async (next: boolean) => {
      if (!selectedId) {
        return
      }
      const item = itemsState.items.find((entry) => entry.id === selectedId)
      if (!item) {
        return
      }
      await handleToggleMyPrep(item, next)
    },
    [handleToggleMyPrep, itemsState.items, selectedId],
  )

  const handleDismissFromDetail = React.useCallback(async () => {
    if (!selectedId) {
      return
    }
    const item = itemsState.items.find((entry) => entry.id === selectedId)
    if (!item) {
      closeDetail()
      return
    }
    await handleDismiss(item)
  }, [closeDetail, handleDismiss, itemsState.items, selectedId])

  const handleArchiveFromDetail = React.useCallback(async () => {
    if (!selectedId) {
      return
    }
    const item = itemsState.items.find((entry) => entry.id === selectedId)
    if (!item) {
      closeDetail()
      return
    }
    await handleArchive(item)
  }, [closeDetail, handleArchive, itemsState.items, selectedId])

  const renderPrepGrid = React.useCallback(
    (
      options: {
        forceSingleColumn?: boolean
        scrollContainer?: HTMLDivElement | null
        emptyState?: React.ReactNode
      } = {},
    ) => {
      if (myOnly && myOnlyLoading) {
        return <MyPrepListSkeleton rows={myPrepSkeletonRows} />
      }

      const grid = (
        <PrepGrid
          items={listItems}
          getKey={(item) => item.id}
          forceSingleColumn={options.forceSingleColumn}
          renderItem={(item) =>
            myOnly ? (
              <MyPrepSortableCard
                item={item}
                lineClamp={lineClamp}
                isSuperadmin={isSuperadmin}
                onOpenDetail={handleSelectPrepItem}
                onToggleMyPrep={handleToggleMyPrep}
                onDismiss={handleDismiss}
                onArchive={isSuperadmin ? handleArchive : undefined}
                onAddToShowClock={handleOpenAddToShowClock}
                launchedFromMyPrep={true}
                onHoverPrefetch={prefetchDetail}
                selected={effectiveLayout === 'single' ? item.id === selectedId : undefined}
                disableReorder={reorderDisabled}
                onKeyboardReorder={handleKeyboardReorder}
              />
            ) : (
              <PrepCard
                item={item}
                lineClamp={lineClamp}
                isSuperadmin={isSuperadmin}
                onOpenDetail={handleSelectPrepItem}
                onToggleMyPrep={handleToggleMyPrep}
                onDismiss={handleDismiss}
                onArchive={isSuperadmin ? handleArchive : undefined}
                onAddToShowClock={handleOpenAddToShowClock}
                launchedFromMyPrep={false}
                onHoverPrefetch={prefetchDetail}
                selected={effectiveLayout === 'single' ? item.id === selectedId : undefined}
              />
            )
          }
          hasMore={hasMore}
          loadingMore={loadingMore}
          onLoadMore={loadMore}
          scrollContainer={options.scrollContainer}
          emptyState={options.emptyState}
        />
      )

      if (!myOnly) {
        return grid
      }

      return (
        <DndContext
          sensors={sensors}
          disabled={myReordering}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <SortableContext items={listItems.map((item) => item.id)} strategy={verticalListSortingStrategy}>
            {grid}
          </SortableContext>
        </DndContext>
      )
    },
    [
      effectiveLayout,
      handleArchive,
      handleDismiss,
      handleDragCancel,
      handleDragEnd,
      handleDragStart,
      handleKeyboardReorder,
      handleSelectPrepItem,
      handleToggleMyPrep,
      lineClamp,
      hasMore,
      listItems,
      loadingMore,
      loadMore,
      myOnly,
      myReordering,
      prefetchDetail,
      reorderDisabled,
      selectedId,
      sensors,
      isSuperadmin,
      myOnlyLoading,
      myPrepSkeletonRows,
    ],
  )

  const endDrag = React.useCallback(
    (revert?: boolean) => {
      if (dragCleanupRef.current) {
        dragCleanupRef.current()
        dragCleanupRef.current = null
      }
      if (revert && dragStateRef.current) {
        setSingleLayoutSplit(dragStateRef.current.startSplit)
      }
      dragStateRef.current = null
      setIsDraggingSplit(false)
    },
    [setSingleLayoutSplit],
  )

  const beginDrag = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!isMdUp) {
        return
      }
      const container = splitContainerRef.current
      if (!container) {
        return
      }
      event.preventDefault()
      const rect = container.getBoundingClientRect()
      dragStateRef.current = {
        startSplit: singleLayoutSplit,
        width: rect.width,
        left: rect.left,
      }
      setIsDraggingSplit(true)

      const handleMove = (moveEvent: MouseEvent) => {
        if (!dragStateRef.current) {
          return
        }
        const { width, left } = dragStateRef.current
        const offset = moveEvent.clientX - left
        const ratio = clampSplit(offset / width)
        setSingleLayoutSplit(ratio)
      }

      const handleUp = () => {
        endDrag(false)
      }

      const handleBlur = () => {
        endDrag(true)
      }

      const handleKeyDown = (keyEvent: KeyboardEvent) => {
        if (keyEvent.key === 'Escape') {
          keyEvent.preventDefault()
          endDrag(true)
        }
      }

      window.addEventListener('mousemove', handleMove)
      window.addEventListener('mouseup', handleUp)
      window.addEventListener('blur', handleBlur)
      window.addEventListener('keydown', handleKeyDown)

      dragCleanupRef.current = () => {
        window.removeEventListener('mousemove', handleMove)
        window.removeEventListener('mouseup', handleUp)
        window.removeEventListener('blur', handleBlur)
        window.removeEventListener('keydown', handleKeyDown)
      }
    },
    [endDrag, isMdUp, setSingleLayoutSplit, singleLayoutSplit],
  )

  const handleSeparatorKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (!isMdUp) {
        return
      }
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault()
        const delta = event.key === 'ArrowLeft' ? -0.02 : 0.02
        setSingleLayoutSplit(clampSplit(singleLayoutSplit + delta))
      }
      if (event.key === 'Home') {
        event.preventDefault()
        setSingleLayoutSplit(MIN_SPLIT)
      }
      if (event.key === 'End') {
        event.preventDefault()
        setSingleLayoutSplit(MAX_SPLIT)
      }
    },
    [isMdUp, setSingleLayoutSplit, singleLayoutSplit],
  )

  React.useEffect(() => {
    return () => {
      if (dragCleanupRef.current) {
        dragCleanupRef.current()
        dragCleanupRef.current = null
      }
    }
  }, [])

  React.useEffect(() => {
    if (effectiveLayout !== 'single') {
      if (dragCleanupRef.current) {
        dragCleanupRef.current()
        dragCleanupRef.current = null
      }
      dragStateRef.current = null
      setIsDraggingSplit(false)
      setSingleScrollContainer(null)
    }
  }, [effectiveLayout])

  React.useEffect(() => {
    if (effectiveLayout === 'single') {
      viewportScrollRef.current = singleScrollContainer
    } else if (viewportScrollRef.current === singleScrollContainer) {
      viewportScrollRef.current = null
    }
  }, [effectiveLayout, singleScrollContainer])

  React.useEffect(() => {
    if (effectiveLayout === 'single') {
      return
    }
    if (myHydrationInFlightRef.current) {
      return
    }
    if (selectedId !== null && retainedUsedDrawerIdRef.current === selectedId) {
      return
    }
    if (!listLoading && listItems.length === 0 && selectedId !== null) {
      setSelectedPrepId(null, { replace: true })
    }
  }, [effectiveLayout, listItems.length, listLoading, selectedId, setSelectedPrepId])

  const applySelectionForMode = React.useCallback(
    (mode: 'my-only' | 'single', visibleItems: readonly PrepListItem[], decision: SelectionResolution) => {
      if (selectedId !== null && retainedUsedDrawerIdRef.current === selectedId) {
        return
      }
      if (!Array.isArray(visibleItems) || visibleItems.length === 0) {
        if (selectedId !== null) {
          scheduleSelectionUpdate(mode, selectedId, null, 0)
        }
        return
      }

      if (decision === undefined) {
        return
      }

      const target = decision ?? visibleItems[0]?.id ?? null
      if (target === null || target === selectedId) {
        return
      }

      scheduleSelectionUpdate(mode, selectedId, target, visibleItems.length)
    },
    [scheduleSelectionUpdate, selectedId],
  )

  const ensureSelectionForMyOnly = React.useCallback(() => {
    if (!mountedRef.current) {
      return
    }

    const guardSelection =
      myOnly && myCount > 0 && !myHydrated && (myOnlyLoading || listAllMyItems.length === 0)

    if (guardSelection) {
      return
    }

    const ready = !listLoading && !listError
    const decision = resolveSelectionForMyOnly({
      myOnly,
      ready,
      visibleItems: listItems,
      selectedId,
      lastKnownIndex: lastMyOnlyIndexRef.current,
      lastClickedId: lastClickedIdRef.current,
    })

    if (!myOnly || !ready) {
      return
    }

    applySelectionForMode('my-only', listItems, decision)
  }, [
    applySelectionForMode,
    listAllMyItems.length,
    listError,
    listItems,
    listLoading,
    myCount,
    myHydrated,
    myOnlyLoading,
    myOnly,
    selectedId,
  ])

  React.useEffect(() => {
    if (!mountedRef.current) {
      return
    }
    if (effectiveLayout !== 'single') {
      return
    }
    if (myOnly) {
      ensureSelectionForMyOnly()
      return
    }
    const ready = !listLoading && !listError
    const decision = resolveSelectionForSingleLayout({
      isSingleLayout: effectiveLayout === 'single',
      ready,
      visibleItems: listItems,
      selectedId,
      isMyOnly: myOnly,
      lastClickedId: lastClickedIdRef.current,
    })

    if (!ready) {
      return
    }

    applySelectionForMode('single', listItems, decision)
  }, [
    effectiveLayout,
    ensureSelectionForMyOnly,
    applySelectionForMode,
    listError,
    listItems,
    listLoading,
    myOnly,
    selectedId,
  ])

  React.useEffect(() => {
    if (effectiveLayout !== 'single' || !selectedId) {
      setSingleDetailLoading(false)
      setSingleDetailError(null)
      return
    }
    if (detailCacheRef.current.has(selectedId)) {
      setSingleDetailLoading(false)
      setSingleDetailError(null)
      return
    }

    const controller = new AbortController()
    setSingleDetailLoading(true)
    setSingleDetailError(null)

    loadDetail(selectedId, controller.signal)
      .then(() => {
        if (!controller.signal.aborted) {
          setSingleDetailLoading(false)
          setSingleDetailError(null)
        }
      })
      .catch((error) => {
        if (controller.signal.aborted) {
          return
        }
        const requestId = extractRequestId(error)
        const message =
          error && typeof error === 'object' && 'message' in error
            ? String((error as { message?: unknown }).message ?? 'Failed to load prep item.')
            : 'Failed to load prep item.'
        const displayMessage = withRequestId(message, requestId)
        setSingleDetailError(displayMessage)
        setSingleDetailLoading(false)
      })

    return () => {
      controller.abort()
    }
  }, [effectiveLayout, loadDetail, selectedId])

  React.useEffect(() => {
    if (effectiveLayout !== 'single') {
      setSingleDetailLoading(false)
      setSingleDetailError(null)
    }
  }, [effectiveLayout])

  const handleScroll = React.useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const scrollOffset = event.currentTarget.scrollTop
      const diff = Math.abs(scrollOffset - lastScrollOffsetRef.current)
      if (diff > SCROLL_ABORT_THRESHOLD) {
        abortPrefetches()
      }
      lastScrollOffsetRef.current = scrollOffset
    },
    [abortPrefetches],
  )

  React.useEffect(() => {
    if (!myOnly) {
      lastMyOnlyIndexRef.current = null
      return
    }
    if (selectedId === null) {
      return
    }
    const index = listItems.findIndex((item) => item.id === selectedId)
    if (index >= 0) {
      lastMyOnlyIndexRef.current = index
    }
  }, [myOnly, selectedId, listItems])

  React.useEffect(() => {
    if (!myOnly) {
      return
    }
    ensureSelectionForMyOnly()
  }, [ensureSelectionForMyOnly, myOnly])

  const snackbarActionNode = snackbar?.action
    ? snackbar.action === 'undo-dismiss'
      ? (
          <Button color="inherit" size="small" onClick={handleUndoDismiss}>
            Undo
          </Button>
        )
      : snackbar.action === 'undo-my-remove'
        ? (
          <Button color="inherit" size="small" onClick={handleUndoMyRemove}>
            Undo
          </Button>
        )
        : snackbar.action === 'undo-used'
          ? (
              <Button color="inherit" size="small" onClick={handleUndoUsed}>
                Undo
              </Button>
            )
        : undefined
    : undefined

  const isRecoveryView = recoveryOpen
  const activeCategoryState = isRecoveryView ? recoveryCategoryState : categoryState
  const showStandardEmptyState = !isRecoveryView && !myOnly && !listLoading && !listError && listItems.length === 0
  const showMyOnlyEmptyState = !isRecoveryView && myOnly && !listLoading && !listError && listItems.length === 0
  const myOnlyEmptyState = showMyOnlyEmptyState ? (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        gap: 1.5,
        py: 6,
        px: 3,
      }}
    >
      <Typography variant="h6" component="h3">
        No items in My Prep Sheet yet
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 320 }}>
        Tap ★ on any story to add it here.
      </Typography>
      <Button variant="outlined" size="small" onClick={exitMyOnly}>
        Browse all stories
      </Button>
    </Box>
  ) : null
  const shouldRenderGrid =
    !isRecoveryView &&
    ((myOnly && !listError && (myOnlyLoading || !listLoading)) ||
      (!myOnly && !listLoading && !listError && (listItems.length > 0 || myOnly)))
  const detailDrawerOpen =
    !isRecoveryView && effectiveLayout === 'columns'
      ? isMdUp
        ? Boolean(selectedId)
        : shouldOpenDrawerOnMobile({ isDesktop: isMdUp, selectedId })
      : false
  const detailEmptyState = myOnly ? (
    <Stack spacing={1} sx={{ maxWidth: 360 }}>
      <Typography variant="h6" component="h3">
        Select a saved story
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {listItems.length === 0
          ? 'No items in My Prep Sheet yet. Tap ★ on any story to add it.'
          : 'Choose a saved story from the list to see its details.'}
      </Typography>
    </Stack>
  ) : (
    <Typography variant="body1" color="text.secondary">
      Select a prep item to view its details.
    </Typography>
  )

  React.useEffect(() => {
    if (process.env.NODE_ENV !== 'development') {
      return
    }
    if (typeof document === 'undefined') {
      return
    }
    if (hasDevCheckedGridRef.current) {
      return
    }

    const grid = document.getElementById('prep-grid')
    if (!grid) {
      return
    }

    hasDevCheckedGridRef.current = true

    try {
      console.log('[prep] tracks:', getComputedStyle(grid).gridTemplateColumns)

      let ancestor: HTMLElement | null = grid
      while (ancestor) {
        const styles = getComputedStyle(ancestor)
        if (parseFloat(styles.width) === 0 || parseFloat(styles.height) === 0) {
          console.warn('[prep] zero-size ancestor', ancestor, { w: styles.width, h: styles.height })
          break
        }
        ancestor = ancestor.parentElement
      }

      Array.from(grid.children).forEach((element, index) => {
        const styles = getComputedStyle(element as HTMLElement)
        console.log(`[prep] item ${index} width:`, styles.width)
      })
    } catch (error) {
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('[prep] failed to inspect grid ancestors', error)
      }
    }
  }, [itemsState.items.length])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        width: '100%',
        position: 'relative',
      }}
    >
      <Stack spacing={0.75} sx={{ flex: 1, minHeight: 0 }}>
        {activeCategoryState.error ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {activeCategoryState.error}
            </Typography>
            <Button size="small" variant="text" onClick={handleCategoryManualRetry}>
              Retry
            </Button>
          </Stack>
        ) : null}
        {!isRecoveryView && !activeCategoryState.error && categoryRetryReady ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Categories did not load. Retry?
            </Typography>
            <Button size="small" variant="text" onClick={handleCategoryManualRetry}>
              Retry
            </Button>
          </Stack>
        ) : null}

        <Box sx={{ flex: '0 0 auto', p: 0, m: 0 }}>
          <CategoryPills
            categories={activeCategoryState.items}
            loading={activeCategoryState.loading}
            emptyMessage="No categories yet"
            searchValue={searchInput}
            onSearchChange={(nextValue) => setSearchInput(nextValue)}
            searchPlaceholder="Search headline or tease"
            onMyPrepToggleStart={handleMyPrepToggleStart}
            onMyPrepToggled={handleMyPrepToggled}
            onMyPrepToggleError={handleMyPrepToggleError}
            getMyPrepEnterSelection={getMyPrepEnterSelection}
            onRecoveryToggle={handleRecoveryToggle}
            recoveryActive={recoveryOpen}
            disableMyPrepToggle={recoveryOpen}
            onNotify={(message, severity) => setSnackbar({ message, severity })}
          />
        </Box>

        <Paper
          variant="outlined"
          sx={{
            borderRadius: shapeBorderRadius,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          {isRecoveryView ? (
            <PrepRecoveryShell
              isSuperadmin={isSuperadmin}
              forceSingleColumn={effectiveLayout === 'single'}
              onCategoriesLoading={handleRecoveryCategoriesLoading}
              onCategoriesUpdate={handleRecoveryCategoriesUpdate}
              onNotify={(message, severity) => setSnackbar({ message, severity })}
            />
          ) : (
            <>
              {listLoading && !myOnly ? (
                <Stack spacing={2} sx={{ p: 3 }}>
                  {Array.from({ length: skeletonCount }).map((_, index) => (
                    <Skeleton key={index} variant="rounded" height={140} sx={{ borderRadius: shapeBorderRadius }} />
                  ))}
                </Stack>
              ) : null}

              {!listLoading && listError ? (
                <Box sx={{ p: 3 }}>
                  <Alert severity="error">{listError}</Alert>
                </Box>
              ) : null}

              {showStandardEmptyState ? (
                <Box sx={{ p: 3 }}>
                  <Typography variant="body1" color="text.secondary">
                    No prep items match your filters yet. Try clearing the filters or adjusting your search.
                  </Typography>
                </Box>
              ) : null}

              {shouldRenderGrid ? (
                effectiveLayout === 'single' ? (
                  <Box
                    ref={splitContainerRef}
                    sx={{
                      display: { xs: 'block', md: 'grid' },
                      gridTemplateColumns: { md: `${Math.round(singleLayoutSplit * 100)}% 8px 1fr` },
                      gridTemplateRows: { md: 'minmax(0, 1fr)' },
                      columnGap: 0,
                      rowGap: 0,
                      alignItems: 'stretch',
                      justifyItems: 'stretch',
                      width: '100%',
                      height: { md: 'calc(100vh - var(--prepHeaderAndFilters, 160px))' },
                      minHeight: { md: 'calc(100vh - var(--prepHeaderAndFilters, 160px))' },
                      maxHeight: { md: 'calc(100vh - var(--prepHeaderAndFilters, 160px))' },
                      overflow: 'hidden',
                      borderRadius: 0,
                    }}
                  >
                    <Box
                      ref={handleSingleScrollContainerRef}
                      onScroll={handleScroll}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        minWidth: 0,
                        minHeight: 0,
                        height: { md: '100%' },
                        borderRight: { md: '1px solid', xs: 0 },
                        borderColor: { md: 'divider' },
                        overflowY: { md: 'auto', xs: 'visible' },
                        overflowX: 'hidden',
                        borderRadius: 0,
                      }}
                    >
                      <Box sx={{ flex: 1, minHeight: 0 }}>
                        {renderPrepGrid({
                          forceSingleColumn: true,
                          scrollContainer: singleScrollContainer,
                          emptyState: myOnly ? myOnlyEmptyState : null,
                        })}
                      </Box>
                    </Box>
                    <Box
                      role="separator"
                      aria-orientation="vertical"
                      aria-label="Resize detail pane"
                      aria-valuemin={Math.round(MIN_SPLIT * 100)}
                      aria-valuemax={Math.round(MAX_SPLIT * 100)}
                      aria-valuenow={Math.round(singleLayoutSplit * 100)}
                      tabIndex={0}
                      onKeyDown={handleSeparatorKeyDown}
                      onMouseDown={beginDrag}
                      sx={{
                        display: { xs: 'none', md: 'block' },
                        width: 8,
                        minWidth: 8,
                        maxWidth: 8,
                        cursor: 'col-resize',
                        backgroundColor: isDraggingSplit ? 'action.selected' : 'transparent',
                        '&:hover': { backgroundColor: 'action.hover' },
                        height: '100%',
                      }}
                    />
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        minWidth: 0,
                        minHeight: 0,
                        overflowY: { md: 'auto', xs: 'visible' },
                        overflowX: 'hidden',
                        borderLeft: { md: '1px solid', xs: 0 },
                        borderColor: { md: 'divider' },
                        p: { xs: 2, md: 3 },
                        borderRadius: 0,
                      }}
                    >
                      {detailItem ? (
                        <PrepDetailContent
                          item={detailItem}
                          loading={singleDetailLoading && !selectedDetail && !selectedListItem}
                          error={singleDetailError}
                          onToggleMyPrep={handleToggleMyPrepFromDetail}
                          onMarkUsed={handleMarkUsedFromDetail}
                          onDismiss={handleDismissFromDetail}
                          onArchive={isSuperadmin ? handleArchiveFromDetail : undefined}
                          onAddToShowClock={handleOpenAddToShowClock}
                          launchedFromMyPrep={myOnly}
                          isSuperadmin={isSuperadmin}
                        />
                      ) : (
                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                          {detailEmptyState}
                        </Box>
                      )}
                    </Box>
                  </Box>
                ) : (
                  <PrepListViewport onScroll={handleScroll}>
                    {({ scrollContainer }) => {
                      if (scrollContainer) {
                        viewportScrollRef.current = scrollContainer
                      }

                      return renderPrepGrid({
                        forceSingleColumn: myOnly,
                        scrollContainer,
                        emptyState: myOnly ? myOnlyEmptyState : null,
                      })
                    }}
                  </PrepListViewport>
                )
              ) : null}
            </>
          )}
        </Paper>
      </Stack>

      <Box
        component="div"
        role="status"
        aria-live="polite"
        sx={{
          position: 'absolute',
          width: 1,
          height: 1,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          clipPath: 'inset(50%)',
        }}
      >
        {ariaLiveMessage}
      </Box>

      {!isRecoveryView && effectiveLayout === 'columns' ? (
          <PrepDetailDrawer
            open={detailDrawerOpen}
            onClose={closeDetail}
            prepId={selectedId ?? null}
            cached={cachedDetail}
            fallbackItem={selectedListItem}
            onToggleMyPrep={handleToggleMyPrepFromDetail}
            onMarkUsed={handleMarkUsedFromDetail}
            onDismiss={handleDismissFromDetail}
            onArchive={isSuperadmin ? handleArchiveFromDetail : undefined}
            onAddToShowClock={handleOpenAddToShowClock}
            launchedFromMyPrep={myOnly}
            isSuperadmin={isSuperadmin}
            emptyContent={detailEmptyState}
            navIds={myOnly ? myPrepNavIds : []}
            onNavigate={(nextId) => {
              retainedUsedDrawerIdRef.current = null
              setSelectedPrepId(nextId, { replace: true })
            }}
            onDetailLoaded={(detail) => {
              detailCacheRef.current.set(detail.id, detail)
            }}
          />
      ) : null}

      {!isRecoveryView ? (
        <AddPrepToShowClockModal
          open={Boolean(addToShowClockTarget)}
          stationId={stationId}
          prepItemId={addToShowClockTarget?.prepId ?? 0}
          prepHeadline={addToShowClockTarget?.prepHeadline ?? 'Prep item'}
          launchedFromMyPrep={addToShowClockTarget?.launchedFromMyPrep ?? false}
          onClose={() => setAddToShowClockTarget(null)}
          onSuccessToast={(message) => setSnackbar({ message, severity: 'success' })}
        />
      ) : null}

      <Snackbar open={Boolean(snackbar)} autoHideDuration={5000} onClose={handleCloseSnackbar}>
        {snackbar ? (
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} action={snackbarActionNode} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        ) : null}
      </Snackbar>
    </Box>
  )
}

export default PrepListPage
